// ===================================================================
// data/songData.ts
// Chansons en bambara pour l'analyse musicale interactive
// ===================================================================

export type GrammaticalCategory =
  | "pronom"
  | "verbe"
  | "nom"
  | "postposition"
  | "adjectif"
  | "marqueur"
  | "connecteur"
  | "adverbe"
  | "determinant"
  | "exclamation";

export interface WordAnnotation {
  translation: string;
  translationEn?: string;
  grammaticalCategory: GrammaticalCategory;
  grammarRule: string;
  example: string;
  exampleTranslation: string;
  exampleTranslationEn?: string;
}

export interface LyricLine {
  words: string[];
  translation: string;
  translationEn?: string;
  grammarNote?: string;
  structure?: string;
  sectionTitle?: string;
}

export interface BambaraSong {
  id: string;
  title: string;
  artist: string;
  genre: string;
  theme: string;
  emoji: string;
  difficulty: "debutant" | "intermediaire" | "avance";
  lines: LyricLine[];
  wordAnnotations: Record<string, WordAnnotation>;
}

// ─── Annotations communes (partagées entre chansons) ──────────────
const COMMON_ANNOTATIONS: Record<string, WordAnnotation> = {
  N: {
    translation: "je / moi",
    grammaticalCategory: "pronom",
    grammarRule: `N est le pronom de la 1ere personne du singulier en bambara. Il peut aussi s'ecrire "Ne" en position accentuee.`,
    example: "N be taa.",
    exampleTranslation: "Je pars.",
  },
  Ne: {
    translation: "moi / je (forme accentuee)",
    grammaticalCategory: "pronom",
    grammarRule: "Ne est la forme accentuee ou de sujet du pronom de 1ere personne singulier. Utilisee en debut de phrase ou apres pause.",
    example: "Ne ce ye.",
    exampleTranslation: "Je suis un homme.",
  },
  A: {
    translation: "il / elle",
    grammaticalCategory: "pronom",
    grammarRule: "A est le pronom de la 3eme personne du singulier (il ou elle, le bambara ne distingue pas les genres).",
    example: "A be kuma.",
    exampleTranslation: "Il/Elle parle.",
  },
  U: {
    translation: "ils / elles",
    grammaticalCategory: "pronom",
    grammarRule: "U est le pronom de la 3eme personne du pluriel.",
    example: "U be taa.",
    exampleTranslation: "Ils/Elles partent.",
  },
  I: {
    translation: "tu / toi",
    grammaticalCategory: "pronom",
    grammarRule: "I est le pronom de la 2eme personne du singulier.",
    example: "I be baara ke wa?",
    exampleTranslation: "Tu travailles ?",
  },
  "bɛ": {
    translation: "(marqueur present inaccompli)",
    grammaticalCategory: "marqueur",
    grammarRule: "BE est le marqueur de l'aspect inaccompli (present, habituel). Structure : Sujet + BE + Verbe → action en cours ou habituelle.",
    example: "N be dun.",
    exampleTranslation: "Je mange / Je suis en train de manger.",
  },
  ye: {
    translation: "(marqueur passe accompli / attributif)",
    grammaticalCategory: "marqueur",
    grammarRule: `YE a deux usages : (1) marqueur d'accompli passe — Sujet + YE + Verbe ; (2) copule attributive en fin de groupe nominal — Sujet + Attribut + YE (= "est").`,
    example: "A ye taa. / Ne ce ye.",
    exampleTranslation: "Il est parti. / Je suis un homme.",
  },
  "tɛ": {
    translation: "(marqueur negation present)",
    grammaticalCategory: "marqueur",
    grammarRule: "TE est la forme negative de BE (present). Structure : Sujet + TE + Verbe → negation au present.",
    example: "N te taa.",
    exampleTranslation: "Je ne pars pas.",
  },
  ka: {
    translation: "(marqueur qualite permanente)",
    grammaticalCategory: "marqueur",
    grammarRule: "KA indique une qualite permanente ou un etat stable. Structure : Sujet + KA + Adjectif. Different de BE (etat temporaire).",
    example: "Duniya ka nyi.",
    exampleTranslation: "La vie est belle (par nature).",
  },
  tun: {
    translation: "(marqueur passe revolu)",
    grammaticalCategory: "marqueur",
    grammarRule: "TUN place apres le verbe indique un passe revolu (imparfait ou plus-que-parfait). Structure : Sujet + YE + Verbe + TUN.",
    example: "A ye taa tun.",
    exampleTranslation: "Il etait parti / Il avait ete parti.",
  },
  la: {
    translation: "a / sur / dans (postposition)",
    grammaticalCategory: "postposition",
    grammarRule: "LA est une postposition locative. En bambara, les postpositions se placent APRES le nom (contrairement au francais). Structure : Nom + LA.",
    example: "Sugu la fen bee be yen.",
    exampleTranslation: "Au marche, tout est la.",
  },
  "kɔnɔ": {
    translation: "dans (postposition) / oiseau (nom)",
    grammaticalCategory: "postposition",
    grammarRule: "KONO comme postposition signifie \"dans, a l'interieur de\". Structure : Nom + KONO. Comme nom, il signifie \"oiseau\".",
    example: "So kono ne be sigi.",
    exampleTranslation: "Dans la maison, je reste.",
  },
  "fɛ": {
    translation: "pres de / a cote de (postposition)",
    grammaticalCategory: "postposition",
    grammarRule: "FE est une postposition de proximite. Structure : Nom + FE.",
    example: "Jiri fe u be sigi.",
    exampleTranslation: "Pres de l'arbre, ils s'assoient.",
  },
  taa: {
    translation: "aller / partir",
    grammaticalCategory: "verbe",
    grammarRule: "TAA est un verbe de mouvement. Il est souvent utilise avec BE (present) ou YE (passe accompli).",
    example: "N ye taa sira la.",
    exampleTranslation: "Je suis parti sur le chemin.",
  },
  dun: {
    translation: "manger / entrer / connaitre (selon contexte)",
    grammaticalCategory: "verbe",
    grammarRule: "DUN peut signifier \"manger\" ou \"entrer\". Le contexte determine le sens. Comme \"manger\" : A ye kono dun (Il a mange l'oiseau).",
    example: "U ye dun.",
    exampleTranslation: "Ils ont mange.",
  },
  "kɛ": {
    translation: "faire",
    grammaticalCategory: "verbe",
    grammarRule: "KE est un verbe generique tres courant signifiant \"faire\". Il est utilise dans de nombreuses expressions composees.",
    example: "Baara ke ka ni.",
    exampleTranslation: "Faire du travail est bon.",
  },
  kuma: {
    translation: "parler / parole",
    grammaticalCategory: "verbe",
    grammarRule: "KUMA est a la fois un verbe (\"parler\") et un nom (\"parole, mot\"). Verbe : A be kuma (Il parle).",
    example: "A be kuma bambara kan na.",
    exampleTranslation: "Il parle en bambara.",
  },
  sigi: {
    translation: "s'asseoir / rester / habiter",
    grammaticalCategory: "verbe",
    grammarRule: "SIGI signifie \"s'asseoir\", \"rester\" ou \"habiter\" selon le contexte.",
    example: "I be sigi yan.",
    exampleTranslation: "Tu restes ici.",
  },
  "sɔrɔ": {
    translation: "trouver / obtenir / avoir",
    grammaticalCategory: "verbe",
    grammarRule: "SORO signifie \"trouver\" ou \"obtenir\". Forme passive avec -ra : sorora (a ete trouve/obtenu).",
    example: "N ye fen soro.",
    exampleTranslation: "J'ai trouve quelque chose.",
  },
  "dùniya": {
    translation: "la vie / le monde",
    grammaticalCategory: "nom",
    grammarRule: "DUNIYA (aussi dunya) est un emprunt a l'arabe \"dunya\" (monde terrestre). Tres courant dans la musique et la poesie bambara.",
    example: "Duniya ka nyi kosebe.",
    exampleTranslation: "La vie est tres belle.",
  },
  so: {
    translation: "maison / cheval",
    grammaticalCategory: "nom",
    grammarRule: "SO signifie \"maison\" ou \"cheval\" selon le contexte et les tons. Le ton distingue les deux sens.",
    example: "So kono ne be sigi.",
    exampleTranslation: "Dans la maison, je reste.",
  },
  sira: {
    translation: "chemin / route / voie",
    grammaticalCategory: "nom",
    grammarRule: "SIRA designe un chemin, une route ou une voie. Employe aussi au sens figure (\"la bonne voie\").",
    example: "Sira ka jan.",
    exampleTranslation: "La route est longue.",
  },
  tile: {
    translation: "soleil / journee",
    grammaticalCategory: "nom",
    grammarRule: "TILE signifie \"soleil\" ou \"journee\" (le soleil qui marque le jour). TILE BEE = \"chaque jour, tous les jours\".",
    example: "Tile be yele.",
    exampleTranslation: "Le soleil brille.",
  },
  "yɛlɛ": {
    translation: "briller / rire / lumiere",
    grammaticalCategory: "verbe",
    grammarRule: "YELE signifie \"briller\" (lumiere), \"rire\" ou \"etre joyeux\". Le contexte indique le sens.",
    example: "Tile be yele.",
    exampleTranslation: "Le soleil brille.",
  },
  nyi: {
    translation: "bien / bon / beau",
    grammaticalCategory: "adjectif",
    grammarRule: "NYI (aussi ni) signifie \"bon, bien, beau\". Souvent utilise avec KA : Sujet + KA + NYI.",
    example: "So ka nyi.",
    exampleTranslation: "La maison est belle.",
  },
  "ɲi": {
    translation: "bon / bien / beau",
    grammaticalCategory: "adjectif",
    grammarRule: "NI est une variante de NYI. Souvent utilise avec KA pour exprimer une qualite permanente : A ka ni (C'est bon).",
    example: "Baara ka ni.",
    exampleTranslation: "Le travail est bon.",
  },
  fa: {
    translation: "pere",
    grammaticalCategory: "nom",
    grammarRule: "FA designe le pere. La relation de possession se marque par une postposition : N fa (mon pere).",
    example: "N fa ye na.",
    exampleTranslation: "Mon pere est venu.",
  },
  ba: {
    translation: "mere / fleuve / grand(e)",
    grammaticalCategory: "nom",
    grammarRule: "BA peut signifier \"mere\", \"fleuve\" ou (adjectif) \"grand, grande\". Le ton differencie les sens.",
    example: "N ba be so kono.",
    exampleTranslation: "Ma mere est dans la maison.",
  },
  ani: {
    translation: "et / avec",
    grammaticalCategory: "connecteur",
    grammarRule: "ANI est la conjonction de coordination principale en bambara, equivalant a \"et\" ou \"avec\" en francais.",
    example: "Fa ani ba be yen.",
    exampleTranslation: "Papa et maman sont la.",
  },
  yen: {
    translation: "la / y",
    grammaticalCategory: "adverbe",
    grammarRule: "YEN est un adverbe de lieu signifiant \"la, y\". Utilise aussi avec BE pour indiquer la presence : A be yen (Il est la).",
    example: "U be yen.",
    exampleTranslation: "Ils sont la.",
  },
  yan: {
    translation: "ici",
    grammaticalCategory: "adverbe",
    grammarRule: "YAN est un adverbe de lieu signifiant \"ici\". Oppose a YEN (\"la\").",
    example: "N be sigi yan.",
    exampleTranslation: "Je reste ici.",
  },
  "bɛɛ": {
    translation: "tout / tous / chaque",
    grammaticalCategory: "determinant",
    grammarRule: "BEE signifie \"tout, tous, chaque\". Se place apres le nom : Fen bee (toutes les choses) ; Tile bee (chaque jour).",
    example: "Fen bee ka nyi.",
    exampleTranslation: "Tout est bien.",
  },
  baara: {
    translation: "travail / travailler",
    grammaticalCategory: "nom",
    grammarRule: "BAARA est a la fois un nom (\"travail\") et un verbe (\"travailler\"). Pour le verbe, on utilise souvent \"baara ke\" (faire du travail = travailler).",
    example: "N be baara ke tile bee.",
    exampleTranslation: "Je travaille chaque jour.",
  },
  wari: {
    translation: "argent",
    grammaticalCategory: "nom",
    grammarRule: "WARI designe l'argent, la monnaie. Frequent dans les chansons liees au commerce et a la vie quotidienne.",
    example: "Wari te fen bee ye.",
    exampleTranslation: "L'argent n'est pas tout.",
  },
  muso: {
    translation: "femme / epouse",
    grammaticalCategory: "nom",
    grammarRule: "MUSO designe une femme adulte ou une epouse. Oppose a CE (homme). Egalement utilise dans DENMUSO (jeune fille).",
    example: "Muso nin ka ni.",
    exampleTranslation: "Cette femme est bien.",
  },
  "cɛ": {
    translation: "homme / mari",
    grammaticalCategory: "nom",
    grammarRule: "CE designe un homme adulte ou un mari. Oppose a MUSO (femme). Se retrouve dans des composes : CEkoroba (vieil homme).",
    example: "Ce nin be baara ke.",
    exampleTranslation: "Cet homme travaille.",
  },
  "denmisɛn": {
    translation: "jeune fille / jeune personne",
    grammaticalCategory: "nom",
    grammarRule: "DENMISEN (= den + misen) signifie litteralement \"petit enfant\" mais designe souvent une jeune fille ou un jeune homme.",
    example: "Denmisen be wuli.",
    exampleTranslation: "La jeune fille se leve.",
  },
  "diɲɛ": {
    translation: "monde / vie (variante de duniya)",
    grammaticalCategory: "nom",
    grammarRule: "DINE est un synonyme de DUNIYA, designant le monde ou la vie terrestre. Utilise surtout dans les dialectes du nord.",
    example: "Dine bee ka nyi.",
    exampleTranslation: "Tout le monde est beau.",
  },
  sugu: {
    translation: "marche",
    grammaticalCategory: "nom",
    grammarRule: "SUGU designe le marche, lieu central de la vie sociale au Mali. On dit \"sugu la\" (au marche) avec la postposition LA.",
    example: "N ye taa sugu la.",
    exampleTranslation: "Je suis alle au marche.",
  },
  "fɛn": {
    translation: "chose / quelque chose",
    grammaticalCategory: "nom",
    grammarRule: "FEN designe une chose, un objet. FEN BEE = \"toutes les choses / tout\". FEN SI = \"rien\" (dans les negations).",
    example: "N ye fen soro sugu la.",
    exampleTranslation: "J'ai trouve quelque chose au marche.",
  },
  "kosɛbɛ": {
    translation: "beaucoup / tres / vraiment",
    grammaticalCategory: "adverbe",
    grammarRule: "KOSEBE est l'adverbe d'intensite principal en bambara, equivalant a \"tres, beaucoup, vraiment\". Il se place en fin de phrase ou de groupe.",
    example: "A ka nyi kosebe.",
    exampleTranslation: "C'est vraiment bien.",
  },
  "nɔgɔn": {
    translation: "l'un l'autre / mutuellement / ensemble",
    grammaticalCategory: "adverbe",
    grammarRule: "NOGON exprime la reciprocite. Utilise dans les phrases avec des verbes d'action mutuelle.",
    example: "U be nogon kanu.",
    exampleTranslation: "Ils s'aiment mutuellement.",
  },
  kanu: {
    translation: "aimer / amour",
    grammaticalCategory: "verbe",
    grammarRule: "KANU signifie \"aimer\" (de maniere affective ou romantique) et aussi \"amour\" comme nom. Frequent dans la poesie et la chanson.",
    example: "N be i kanu.",
    exampleTranslation: "Je t'aime.",
  },
  wuli: {
    translation: "se lever / partir / commencer",
    grammaticalCategory: "verbe",
    grammarRule: "WULI signifie \"se lever\" (du lit, d'un siege) ou \"partir\" (au sens de demarrer). Oppose de SIGI (s'asseoir).",
    example: "Tile wulila.",
    exampleTranslation: "Le soleil s'est leve.",
  },
  diya: {
    translation: "etre agreable / plaire / etre heureux",
    grammaticalCategory: "verbe",
    grammarRule: "DIYA signifie \"etre agreable, plaire\". \"N diya\" = \"je suis content/heureux\". Frequent pour exprimer le bien-etre.",
    example: "N diya kosebe.",
    exampleTranslation: "Je suis tres heureux.",
  },
  san: {
    translation: "ciel / an / annee",
    grammaticalCategory: "nom",
    grammarRule: "SAN signifie \"ciel\" ou \"an/annee\". Le contexte precise le sens. SAN KELEN = \"un an\".",
    example: "San ka jan.",
    exampleTranslation: "Le ciel est haut.",
  },
  dugukolo: {
    translation: "terre / sol / la Terre",
    grammaticalCategory: "nom",
    grammarRule: "DUGUKOLO (dugu + kolo) signifie litteralement \"os du village\" → la terre, le sol. Symbolise les racines et l'appartenance.",
    example: "Dugukolo ka bon.",
    exampleTranslation: "La terre est grande.",
  },
  waati: {
    translation: "temps / moment / epoque",
    grammaticalCategory: "nom",
    grammarRule: "WAATI designe le temps, le moment. Emprunt a l'arabe \"waqt\". WAATI MIN = \"le moment ou\".",
    example: "Waati be taa.",
    exampleTranslation: "Le temps passe.",
  },
  "kɛlɛ": {
    translation: "guerre / combat",
    grammaticalCategory: "nom",
    grammarRule: "KELE signifie \"guerre\" ou \"combat\". Comme numerale (kelen), il signifie \"un\". Ne pas confondre les deux.",
    example: "Kele te nyi.",
    exampleTranslation: "La guerre n'est pas bonne.",
  },
  "hɛrɛ": {
    translation: "paix / salut / bien-etre",
    grammaticalCategory: "nom",
    grammarRule: "HERE designe la paix, le bien-etre, le salut. Utilise dans les salutations : \"I ka here ?\" (Tu vas bien ?).",
    example: "Here ka nyi.",
    exampleTranslation: "La paix est bonne.",
  },
  fanga: {
    translation: "force / pouvoir / puissance",
    grammaticalCategory: "nom",
    grammarRule: "FANGA designe la force physique ou le pouvoir politique. \"Fanga ke\" = \"exercer le pouvoir\".",
    example: "A ka fanga bon.",
    exampleTranslation: "Sa force est grande.",
  },
  jiri: {
    translation: "arbre / bois",
    grammaticalCategory: "nom",
    grammarRule: "JIRI designe un arbre ou du bois. Symbole de force et de duree dans la culture bambara.",
    example: "Jiri ka bon.",
    exampleTranslation: "L'arbre est grand.",
  },
  ji: {
    translation: "eau / riviere",
    grammaticalCategory: "nom",
    grammarRule: "JI designe l'eau ou une riviere. Element vital dans la culture sahelienne. JI LA = \"dans l'eau\".",
    example: "Ji be nin la.",
    exampleTranslation: "Il y a de l'eau ici.",
  },
  "kɔ": {
    translation: "derriere / apres (postposition)",
    grammaticalCategory: "postposition",
    grammarRule: "KO comme postposition signifie \"derriere\" ou \"apres\". Nom KO = dos, affaire passee.",
    example: "So ko ne be.",
    exampleTranslation: "Je suis derriere la maison.",
  },
  laban: {
    translation: "fin / dernier / au bout",
    grammaticalCategory: "nom",
    grammarRule: "LABAN designe la fin, la conclusion. \"Laban na\" = \"a la fin, finalement\".",
    example: "Laban na, here sorora.",
    exampleTranslation: "Finalement, la paix a ete trouvee.",
  },
  na: {
    translation: "venir",
    grammaticalCategory: "verbe",
    grammarRule: "NA signifie \"venir\". Oppose de TAA (aller). Peut aussi etre une postposition signifiant \"dans, en (langue)\".",
    example: "A ye na yan.",
    exampleTranslation: "Il est venu ici.",
  },
  don: {
    translation: "jour / connaitre / entrer",
    grammaticalCategory: "verbe",
    grammarRule: "DON a plusieurs sens : \"jour\" (nom), \"connaitre\" (verbe cognitif), \"entrer\" (verbe de mouvement). Le contexte precise.",
    example: "I be ne don wa?",
    exampleTranslation: "Tu me connais ?",
  },
};

// ─── CHANSONS ─────────────────────────────────────────────────────

export const BAMBARA_SONGS: BambaraSong[] = [
  // ── Chanson 1 : Duniya ──────────────────────────────────────────
  {
    id: "duniya",
    title: "Duniya",
    artist: "Style Wassoulou traditionnel",
    genre: "Wassoulou",
    theme: "La beaute de la vie",
    emoji: "🌍",
    difficulty: "debutant",
    lines: [
      {
        words: ["N", "bɛ", "taa"],
        translation: "Je pars",
        structure: "Sujet + BE + Verbe",
        grammarNote: "Present inaccompli : action en cours ou habituelle",
      },
      {
        words: ["Sira", "la"],
        translation: "Sur le chemin",
        structure: "Nom + Postposition",
        grammarNote: "Les postpositions se placent APRES le nom en bambara",
      },
      {
        words: ["Tile", "bɛ", "yɛlɛ"],
        translation: "Le soleil brille",
        structure: "Sujet + BE + Verbe",
        grammarNote: "BE avec un verbe = present/inaccompli",
      },
      {
        words: ["Dùniya", "ka", "nyi"],
        translation: "La vie est belle",
        structure: "Sujet + KA + Adjectif",
        grammarNote: "KA exprime une qualite permanente et intrinseque",
      },
      {
        words: ["N", "ye", "a", "je"],
        translation: "Je l'ai vu",
        structure: "Sujet + YE + Verbe",
        grammarNote: "YE = marqueur de l'accompli passe",
      },
      {
        words: ["So", "kɔnɔ"],
        translation: "Dans la maison",
        structure: "Nom + Postposition",
        grammarNote: "KONO = \"dans, a l'interieur de\" (postposition)",
      },
      {
        words: ["Ne", "cɛ", "ye"],
        translation: "Je suis un homme",
        structure: "Sujet + Attribut + YE",
        grammarNote: "YE en fin de groupe = attributif (= \"est, c'est\")",
      },
      {
        words: ["U", "bɛ", "nɔgɔn", "kanu"],
        translation: "Ils s'aiment",
        structure: "Sujet + BE + Verbe",
        grammarNote: "NOGON indique la reciprocite de l'action",
      },
    ],
    wordAnnotations: {
      ...COMMON_ANNOTATIONS,
      je: {
        translation: "voir",
        grammaticalCategory: "verbe",
        grammarRule: "JE (aussi ye/je selon variante) signifie \"voir\". Structure accomplie : N ye a je (Je l'ai vu).",
        example: "I ye ne je.",
        exampleTranslation: "Tu m'as vu.",
      },
    },
  },

  // ── Chanson 2 : So ───────────────────────────────────────────────
  {
    id: "so",
    title: "So",
    artist: "Style Griot traditionnel",
    genre: "Jaliya (Griot)",
    theme: "Le foyer et la famille",
    emoji: "🏡",
    difficulty: "debutant",
    lines: [
      {
        words: ["So", "ka", "nyi"],
        translation: "La maison est belle",
        structure: "Sujet + KA + Adjectif",
        grammarNote: "KA exprime une qualite permanente",
      },
      {
        words: ["Fa", "ani", "ba", "bɛ", "yen"],
        translation: "Papa et maman sont la",
        structure: "Sujet + BE + Adverbe",
        grammarNote: "ANI = \"et\" ; YEN = \"la\" (adverbe de lieu)",
      },
      {
        words: ["U", "ye", "dun"],
        translation: "Ils ont mange",
        structure: "Sujet + YE + Verbe",
        grammarNote: "YE marque l'accompli passe",
      },
      {
        words: ["Denmisɛn", "bɛ", "baara", "kɛ"],
        translation: "La jeune fille travaille",
        structure: "Sujet + BE + Verbe",
        grammarNote: "\"Baara ke\" = \"faire du travail\" = travailler",
      },
      {
        words: ["N", "tɛ", "taa"],
        translation: "Je ne pars pas",
        structure: "Sujet + TE + Verbe",
        grammarNote: "TE = forme negative du present (negation de BE)",
      },
      {
        words: ["N", "bɛ", "sigi", "yan"],
        translation: "Je reste ici",
        structure: "Sujet + BE + Verbe + Adverbe",
        grammarNote: "SIGI = s'asseoir / rester ; YAN = ici",
      },
      {
        words: ["Diɲɛ", "bɛɛ", "ka", "diya"],
        translation: "Tout le monde est heureux",
        structure: "Sujet + KA + Verbe-etat",
        grammarNote: "BEE = \"tout\" ; DIYA = \"etre heureux, plaire\"",
      },
    ],
    wordAnnotations: {
      ...COMMON_ANNOTATIONS,
    },
  },

  // ── Chanson 3 : Baara ────────────────────────────────────────────
  {
    id: "baara",
    title: "Baara",
    artist: "Style contemporain Mande",
    genre: "Mandingo contemporain",
    theme: "La dignite du travail",
    emoji: "⚒️",
    difficulty: "intermediaire",
    lines: [
      {
        words: ["Baara", "ka", "ɲi"],
        translation: "Le travail est bon",
        structure: "Sujet + KA + Adjectif",
        grammarNote: "KA marque la qualite permanente, intrinseque",
      },
      {
        words: ["N", "bɛ", "baara", "kɛ", "tile", "bɛɛ"],
        translation: "Je travaille tous les jours",
        structure: "Sujet + BE + Verbe + Complement",
        grammarNote: "TILE BEE = \"chaque soleil\" = tous les jours",
      },
      {
        words: ["Wari", "sɔrɔra"],
        translation: "L'argent a ete obtenu",
        structure: "Nom + Verbe passif (-ra)",
        grammarNote: "Le suffixe -RA marque le passif ou l'accompli sans sujet explicite",
      },
      {
        words: ["Ne", "muso", "ye"],
        translation: "Je suis une femme",
        structure: "Sujet + Attribut + YE",
        grammarNote: "YE en fin de proposition = copule attributive",
      },
      {
        words: ["N", "ye", "baara", "kɛ", "tun"],
        translation: "J'avais travaille",
        structure: "Sujet + YE + Verbe + TUN",
        grammarNote: "TUN apres le verbe = passe revolu (plus-que-parfait)",
      },
      {
        words: ["U", "bɛ", "kuma"],
        translation: "Ils parlent",
        structure: "Sujet + BE + Verbe",
        grammarNote: "Present simple avec BE",
      },
      {
        words: ["Sugu", "la", "fɛn", "bɛɛ", "bɛ", "yen"],
        translation: "Au marche, tout est la",
        structure: "Nom + LA + Sujet + BE + Adverbe",
        grammarNote: "LA = postposition locative ; FEN BEE = \"toutes les choses\"",
      },
      {
        words: ["Hɛrɛ", "ani", "fanga", "ka", "nyi"],
        translation: "La paix et la force sont bonnes",
        structure: "Sujet + ANI + Sujet + KA + Adjectif",
        grammarNote: "ANI coordonne deux sujets ; KA exprime la qualite",
      },
    ],
    wordAnnotations: {
      ...COMMON_ANNOTATIONS,
      "sɔrɔra": {
        translation: "a ete obtenu / a ete trouve",
        grammaticalCategory: "verbe",
        grammarRule: "SORORA est la forme passive/accomplie de SORO (trouver, obtenir). Le suffixe -RA indique l'accompli passif ou le resultat.",
        example: "Wari sorora.",
        exampleTranslation: "L'argent a ete trouve.",
      },
    },
  },

  // ── Chanson 4 : Here ─────────────────────────────────────────────
  {
    id: "here",
    title: "Hɛrɛ",
    artist: "Style Wassoulou avance",
    genre: "Wassoulou",
    theme: "La paix et la sagesse",
    emoji: "☮️",
    difficulty: "avance",
    lines: [
      {
        words: ["Hɛrɛ", "ka", "nyi", "kosɛbɛ"],
        translation: "La paix est tres bonne",
        structure: "Sujet + KA + Adjectif + Adverbe",
        grammarNote: "KOSEBE = \"tres, vraiment\" (intensificateur en fin de phrase)",
      },
      {
        words: ["Kɛlɛ", "tɛ", "dùniya", "ka", "kɛ"],
        translation: "La guerre ne fait pas avancer le monde",
        structure: "Sujet + TE + Objet + KA + Verbe",
        grammarNote: "TE + KA = negation d'un infinitif ou etat negatif",
      },
      {
        words: ["Waati", "bɛ", "taa"],
        translation: "Le temps passe",
        structure: "Sujet + BE + Verbe",
        grammarNote: "TAA (aller) utilise pour exprimer le passage du temps",
      },
      {
        words: ["Dugukolo", "bɛ", "sigi"],
        translation: "La terre reste / demeure",
        structure: "Sujet + BE + Verbe",
        grammarNote: "SIGI avec BE = \"demeurer, rester\" (permanence)",
      },
      {
        words: ["Jiri", "ani", "ji", "ka", "nyi"],
        translation: "L'arbre et l'eau sont bons",
        structure: "Sujet + ANI + Sujet + KA + Adjectif",
        grammarNote: "ANI relie deux sujets de meme valeur",
      },
      {
        words: ["Ne", "ye", "hɛrɛ", "sɔrɔ", "tun"],
        translation: "J'avais trouve la paix",
        structure: "Sujet + YE + Verbe + Objet + TUN",
        grammarNote: "TUN = passe revolu ; YE = marqueur de l'accompli",
      },
      {
        words: ["San", "bɛ", "kɔnɔ", "bɛ", "diya"],
        translation: "Dans le ciel, il fait bon",
        structure: "Nom + Postposition + BE + Verbe",
        grammarNote: "KONO postpose a SAN = \"dans le ciel\"",
      },
      {
        words: ["Laban", "na", "hɛrɛ", "bɛ", "sɔrɔ"],
        translation: "A la fin, la paix sera trouvee",
        structure: "Adverbe + NA + Sujet + BE + Verbe",
        grammarNote: "LABAN NA = \"a la fin, finalement\" ; BE = futur ou habituel",
      },
    ],
    wordAnnotations: {
      ...COMMON_ANNOTATIONS,
    },
  },

  // ── Chanson 5 : Seya — Oumou Sangaré ────────────────────────────
  // Extrait educatif — fragments partages a des fins d'apprentissage
  {
    id: "seya",
    title: "Seya",
    artist: "Oumou Sangare",
    genre: "Wassoulou",
    theme: "Joie et rassemblement",
    emoji: "🎤",
    difficulty: "intermediaire",
    lines: [
      // ── Refrain ───────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "REFRAIN" },
      {
        words: ["N", "na", "le"],
        translation: "Je suis arrivée (× 3)",
        structure: "Pronom bref + NA + LE (refrain x3)",
        grammarNote: "N = forme courte de NE (je). NA = venir/arriver. LE = particule completive : l'action est accomplie et le resultat est present. Ce refrain repete 3 fois pose le theme central de la chanson.",
      },
      { words: ["n", "na", "le", "seya"], translation: "je suis arrivée avec joie" },
      {
        words: ["An", "n", "na", "le", "seya"],
        translation: "Oh ! je suis arrivée avec joie",
        structure: "Exclamation AN + N NA LE + SEYA",
        grammarNote: "AN ici est une exclamation de joie (\"Oh !\") et non le pronom AN (nous). N NA LE SEYA = refrain central : arrivee + joie. Ce motif structure toute la chanson comme une affirmation festive.",
      },
      // ── Couplet 1 ─────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "COUPLET 1" },
      {
        words: ["Ne", "na", "le"],
        translation: "Je suis venue",
        structure: "Pronom accentue NE + NA + LE",
        grammarNote: "NE = forme accentuee (\"moi, je\") par opposition a N (forme bref). L'accentuation souligne la presence personnelle et victorieuse de la chanteuse.",
      },
      // ── Couplet 2 ─────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "COUPLET 2" },
      {
        words: ["An", "ye", "wa", "mali", "denw", "fɛ", "seya"],
        translation: "Allons chez les Maliens dans la joie",
        structure: "AN YE WA = subjonctif collectif + Lieu + FƐ (postposition)",
        grammarNote: "AN YE WA = \"allons\" (invitation collective au subjonctif bambara). FƐ = postposition \"chez, aupres de\". MALIDENW = gens/enfants du Mali. La joie SEYA est le but du rassemblement.",
      },
      // ── Couplet 3 ─────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "COUPLET 3" },
      {
        words: ["Ko", "sewire", "le", "deni", "nɔ"],
        translation: "Je dis que la petite est bienheureuse",
        structure: "KO (citateur) + SEWIRE + LE (focalisation) + DENI + NƆ",
        grammarNote: "KO = introducteur de discours (\"je dis que\"). SEWIRE = bienheureux/dans la joie (variante Wassoulou de SEWERE). LE focalise l'assertion. DENI = petite enfant. NƆ = la, a cet endroit.",
      },
      // ── Couplet 4 ─────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "COUPLET 4" },
      {
        words: ["Wɛrɛwɛrɛ", "gunguriman", "y'a", "la"],
        translation: "C'est vraiment une excitation intense",
        structure: "WƐRƐWƐRƐ + GUNGURIMAN + Y'A LA (predicatif)",
        grammarNote: "WƐRƐWƐRƐ = excitation intense (onomatopee mimant le mouvement frenetique). GUNGURIMAN = animation extreme. Y'A = YE + A, marqueur predicatif (\"c'est\"). LA = postposition finale.",
      },
      // ── Couplet 5 ─────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "COUPLET 5" },
      {
        words: ["Manamana", "le", "ne", "rɔ"],
        translation: "C'est l'excitation qui me prend",
        structure: "MANAMANA + LE (focalisation) + NE + RƆ (dans)",
        grammarNote: "MANAMANA = frisson de joie, excitation qui envahit le corps (onomatopee). LE focalise le sujet. NE = moi. RƆ = postposition \"dans, en\" : \"en moi / qui m'habite\".",
      },
      // ── Couplet 6 ─────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "COUPLET 6" },
      {
        words: ["wɛrɛwɛrɛ", "le", "cɛnin", "nɔ"],
        translation: "Le bonhomme est agité",
        structure: "WƐRƐWƐRƐ + LE + CƐNIN + NƆ",
        grammarNote: "CƐNIN = jeune homme (cɛ = homme + ni = diminutif affectueux). NƆ = la, chez lui (postposition locative). L'excitation habite le jeune homme.",
      },
      // ── Couplets 7 — 8 ────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "COUPLETS 7 — 8" },
      {
        words: ["Demisɛnw", "ti", "wɛrɛwɛrɛ", "min", "dɔn"],
        translation: "Une excitation jamais connue des enfants",
        structure: "DEMISƐNW + TI (negatif) + WƐRƐWƐRƐ + MIN (relatif) + DƆN",
        grammarNote: "DEMISƐNW = enfants (variante Wassoulou de DENW). TI = marqueur negatif progressif (variante Wassoulou de TƐ). MIN = pronom relatif \"que, qui, dont\". DƆN = connaitre, savoir.",
      },
      {
        words: ["Cɛmisɛnw", "ti", "wusɛwusɛ", "min", "dɔn"],
        translation: "Une agitation jamais connue des gamins",
        structure: "CƐMISƐNW + TI + WUSƐWUSƐ + MIN + DƆN",
        grammarNote: "CƐMISƐNW = jeunes hommes (cɛ + misɛn + w). WUSƐWUSƐ = agitation physique (reduplication mimant le mouvement precipite).",
      },
      // ── Au marché ─────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "AU MARCHÉ" },
      {
        words: ["Ne", "na", "wa", "suguba", "rɔ", "seya"],
        translation: "J'irai au grand marché",
        structure: "NA WA (intentif/futur) + SUGUBA + RƆ (dans)",
        grammarNote: "NA WA = construction intentive : \"je vais aller\". SUGUBA = grand marche (sugu = marche + ba = grand). RƆ = postposition \"dans, a\" indiquant le lieu.",
      },
      {
        words: ["Bamaɲari", "suguba", "rɔ", "nba"],
        translation: "Le grand marché de Bamagnari",
        structure: "BAMAƝARI + SUGUBA + RƆ + NBA (insistance)",
        grammarNote: "BAMAƝARI = Bamagnari, lieu celebre du commerce en Afrique de l'Ouest. NBA = particule affirmative d'insistance : \"vraiment, bien sur\". Renforce l'enthousiasme.",
      },
      // ── Le bazin ──────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "LE BAZIN" },
      {
        words: ["wɛrɛwɛrɛ", "bazɛn", "san"],
        translation: "Acheter du bazin pour s'exhiber",
        structure: "WƐRƐWƐRƐ (exclamatif) + BAZƐN + SAN (acheter)",
        grammarNote: "BAZƐN = tissu damasse precieux, symbole de prestige. SAN = acheter. WƐRƐWƐRƐ exprime ici l'excitation festive de l'acte d'acheter du beau tissu pour se parer.",
      },
      {
        words: ["N", "bi", "t'a", "di", "galadonla", "ma", "seya"],
        translation: "Je l'apporterai à l'indigoterie",
        structure: "BI (futur proche) + T'A DI + GALADONLA + MA (datif)",
        grammarNote: "BI = marqueur de futur proche. T'A DI = contraction de \"ta a di\" : l'apporter. GALADONLA = atelier de teinture a l'indigo. MA = postposition dative (\"a, pour\").",
      },
      {
        words: ["A", "san", "n", "ka", "bazɛnni", "don", "seya"],
        translation: "C'est le bazin d'Asan (la teinturière)",
        structure: "A SAN (Asan) + N KA BAZƐNNI + DON",
        grammarNote: "A SAN = Asan, surnom de la teinturiere. N KA = possessif 1ere personne (mon). BAZƐNNI = mon bazin (bazɛn + ni diminutif affectueux). DON = mettre, porter.",
      },
      {
        words: ["i", "k'a", "kɛ", "lɔmasa", "y'a", "la"],
        translation: "Ne fais-tu pas du lumasa ?",
        structure: "I (tu) + K'A KƐ (faire cela) + LƆMASA",
        grammarNote: "I = tu (2e personne). K'A KƐ = \"ka a ke\" contracte : faire cela. LƆMASA = technique traditionnelle de teinture (batik/lumasa). Question invitant l'artisane a exercer son art.",
      },
      {
        words: ["k'a", "kɛ", "fiisa", "ye", "wa"],
        translation: "Ne le fais-tu pas en couleur rose ?",
        structure: "K'A KƐ + FIISA + YE WA (question rhetorique)",
        grammarNote: "FIISA = meilleur / couleur rose clair (couleur d'elegance tres prisee). YE WA = particule interrogative en fin de phrase. Invitation rhetorique a la teinturiere.",
      },
      {
        words: ["n", "bi", "ta", "n", "ka", "tayɛrikɛ", "sɔrɔ", "seya"],
        translation: "J'irai voir mon tailleur",
        structure: "BI + TA (aller) + N KA TAYƐRIKƐ + SƆRƆ",
        grammarNote: "BI = futur proche. TA = aller, partir vers (Wassoulou). N KA = possessif. TAYƐRIKƐ = celui/celle qui fait le metier de tailleur. SƆRƆ = trouver, rencontrer.",
      },
      {
        words: ["Tayɛri", "Sumayila", "sɔrɔ", "seya"],
        translation: "Voir le tailleur Soumaïla",
        structure: "TAYƐRI + Prenom + SƆRƆ",
        grammarNote: "TAYƐRI = tailleur (emprunt du francais). SUMAYILA = Soumaila, prenom (de l'arabe Ismail). SƆRƆ = rencontrer, aller voir.",
      },
      { words: ["Tayɛri", "Salama", "sɔrɔ", "seya"], translation: "Voir le tailleur Salama" },
      {
        words: ["K'a", "ka", "n", "ka", "bazɛnni", "kalan", "seya"],
        translation: "Pour faire coudre mon bazin",
        structure: "K'A KA (pour que) + N KA BAZƐNNI + KALAN",
        grammarNote: "K'A KA = pour que, afin de (subordonnee finale). N KA BAZƐNNI = mon bazin. KALAN = apprendre ; ici sens artisanal : faire confectionner, faire coudre.",
      },
      {
        words: ["i", "t'a", "kɛ", "maniyɛri", "ye", "wa", "seya"],
        translation: "Ne le fais-tu pas en des manières ?",
        structure: "I + T'A KƐ (negatif faire) + MANIYƐRI + YE WA",
        grammarNote: "T'A KƐ = contraction negative (ti a ke) : ne pas faire. MANIYƐRI = maniere, style, facon (emprunt du francais \"maniere\", integre en bambara).",
      },
      {
        words: ["Ne", "b'o", "don", "n", "kanubagaluw", "ye", "seya"],
        translation: "Je m'habillerai pour mes fans",
        structure: "B'O DON (progressif : mettre) + KANUBAGALUW + YE",
        grammarNote: "B'O DON = contraction de \"bɛ o don\" (je mets cela). DON = mettre, porter un vetement. KANUBAGALUW = fans, bien-aimes (kanu = amour + baga = celui qui + lu/w = pluriel). YE = pour, devant.",
      },
      // ── Coda ──────────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "CODA" },
      {
        words: ["Ne", "na", "wa", "malidenyi", "fɛ", "nba", "seya"],
        translation: "J'irai chez les Maliens",
        structure: "NA WA + MALIDENYI + FƐ + NBA",
        grammarNote: "MALIDENYI = les Maliens (variante dialectale Wassoulou de MALIDENW). NBA = bien sur, vraiment (intensificateur).",
      },
      {
        words: ["Ne", "na", "le", "An", "wasolo", "n", "na", "le", "An", "diɲan"],
        translation: "Je suis arrivée — oh Wassolo ! Je suis arrivée — oh Monde !",
        structure: "Refrain + AN (exclamation) + Noms propres",
        grammarNote: "WASOLO = Wassoulou, region du sud du Mali, berceau de la musique du meme nom. DIƝAN = le monde entier. Oumou proclame son arrivee a sa region natale et au monde entier.",
      },
      {
        words: ["N", "na", "le", "na", "jɔɔn", "bɛɛ", "n'i", "kanuba"],
        translation: "Je suis arrivée. Chaque personne a ses propres fans",
        structure: "Refrain + JƆƆN BƐƐ (chaque personne) + KANUBA",
        grammarNote: "JƆƆN = personne, quelqu'un. BƐƐ = tout, chaque. N'I = ni contracte : chacun et ses propres. KANUBA = fan, bien-aime (kanu = amour + ba = grand/fort).",
      },
      {
        words: ["Madinɛkura", "suguni", "na", "seya"],
        translation: "Le petit marché de nouveau Madina",
        structure: "MADINƐKURA + SUGUNI + NA (aller vers)",
        grammarNote: "MADINƐKURA = Madina la nouvelle (madina = medina en arabe + kura = nouveau). SUGUNI = petit marche (sugu + ni diminutif). NA = aller vers, se diriger.",
      },
      {
        words: ["Ka", "wa", "kunnin", "dan", "seya"],
        translation: "Venue natter mes cheveux",
        structure: "KA WA (finalite) + KUNNIN + DAN",
        grammarNote: "KA WA = pour aller / afin d'aller (subordonnee finale de but). KUNNIN = cheveux, tete (kun = tete + ni = diminutif). DAN = natter, tresser les cheveux.",
      },
      {
        words: ["K'o", "siri", "bobaraba", "kan", "nba", "seya"],
        translation: "Et les passer autour du grand bassin",
        structure: "K'O (finalite) + SIRI + BOBARABA + KAN (sur) + NBA",
        grammarNote: "K'O = pour que (conjonction finale). SIRI = attacher, nouer, passer autour. BOBARABA = grand bassin, grande cuvette. KAN = postposition \"sur, autour de\".",
      },
    ],
    wordAnnotations: {
      ...COMMON_ANNOTATIONS,
      // ── Refrain et mots cles ──────────────────────────────
      seya: {
        translation: "joie / bonheur / felicite",
        grammaticalCategory: "nom",
        grammarRule: "SEYA designe la joie, le bonheur, la felicite. Mot tres expressif en bambara et en dioula, souvent employe comme exclamation de joie ou comme nom dans les chansons Wassoulou.",
        example: "Seya be ne na.",
        exampleTranslation: "La joie est en moi.",
      },
      na: {
        translation: "venir / arriver",
        grammaticalCategory: "verbe",
        grammarRule: "NA est le verbe \"venir / arriver\" en bambara. NA + LE = arrivee avec etat resultant present. Imperatif pluriel : NAWO (na + wo). NA WA = construction intentive futur (\"je vais aller\").",
        example: "N na le.",
        exampleTranslation: "Je suis venue.",
      },
      le: {
        translation: "particule completive / de focalisation",
        grammaticalCategory: "marqueur",
        grammarRule: "LE a deux usages : (1) Apres un verbe — particule completive : \"na le\" = \"je suis venue (et j'y suis)\", etat acquis present. (2) Apres un nom/pronom — focalisation (\"c'est... que\") : \"Ne le\" = \"c'est moi\".",
        example: "N na le. / Ne le ye o ke.",
        exampleTranslation: "Je suis venue. / C'est moi qui ai fait ca.",
      },
      n: {
        translation: "je / mon / ma (forme courte)",
        grammaticalCategory: "pronom",
        grammarRule: "N est la forme courte et non accentuee du pronom de 1ere personne singulier. Sujet (\"n na\" = je viens) ou possessif (\"n ka\" = mon/ma). Forme accentuee : NE.",
        example: "N taa.",
        exampleTranslation: "Je pars.",
      },
      an: {
        translation: "nous / on (pronom) — Oh ! (exclamation)",
        grammaticalCategory: "pronom",
        grammarRule: "AN a deux usages dans cette chanson : (1) Pronom 1ere personne pluriel inclusif : AN YE WA = allons. (2) Exclamation de joie \"Oh !\" dans le refrain Wassoulou.",
        example: "An be taa kelen.",
        exampleTranslation: "Nous partons ensemble.",
      },
      // ── Mots expressifs (onomatopees) ─────────────────────
      "wɛrɛwɛrɛ": {
        translation: "excitation / agitation festive",
        grammaticalCategory: "nom",
        grammarRule: "WƐRƐWƐRƐ = excitation, agitation festive (onomatopee : le son mime le mouvement frenetique). Peut qualifier l'ambiance de fete ou une personne tres animee. Tres expressif dans la chanson Wassoulou.",
        example: "Wɛrɛwɛrɛ be ne na.",
        exampleTranslation: "L'excitation m'envahit.",
      },
      gunguriman: {
        translation: "excitation intense / extreme animation",
        grammaticalCategory: "nom",
        grammarRule: "GUNGURIMAN qualifie une excitation extreme ou quelqu'un de tres anime. Terme expressif du dialecte Wassoulou pour decrire l'etat de fete absolue.",
        example: "A ye gunguriman.",
        exampleTranslation: "C'est une excitation intense.",
      },
      manamana: {
        translation: "frisson de joie / excitation qui prend",
        grammaticalCategory: "nom",
        grammarRule: "MANAMANA = frisson de joie, excitation physique qui envahit le corps (onomatopee expressive). Designe une emotion intense et positive caracteristique du repertoire Wassoulou.",
        example: "Manamana le ne rɔ.",
        exampleTranslation: "C'est le frisson de joie qui me prend.",
      },
      "wusɛwusɛ": {
        translation: "agitation / s'agiter",
        grammaticalCategory: "nom",
        grammarRule: "WUSƐWUSƐ = agitation physique precipitee (reduplication expressive mimant le mouvement). Synonyme de WƐRƐWƐRƐ mais plus axe sur le mouvement du corps.",
        example: "Wusɛwusɛ be ne na.",
        exampleTranslation: "L'agitation m'envahit.",
      },
      // ── Postpositions ──────────────────────────────────────
      "rɔ": {
        translation: "dans / en / à (postposition locative)",
        grammaticalCategory: "postposition",
        grammarRule: "RƆ est la postposition locative principale en bambara : \"dans, en, a l'interieur de\". Se place apres le nom : SUGUBA RƆ = au grand marche. Cf. FƐ (chez), KAN (sur), NƆ (la).",
        example: "So rɔ.",
        exampleTranslation: "Dans la maison.",
      },
      "nɔ": {
        translation: "là / derrière (postposition locative)",
        grammaticalCategory: "postposition",
        grammarRule: "NƆ = postposition locative \"la, derriere, apres\". CƐNIN NƆ = \"chez lui / en lui\" (l'excitation l'habite). Peut aussi valoir comme particule affirmative.",
        example: "A be ne nɔ.",
        exampleTranslation: "Il est derriere moi.",
      },
      kan: {
        translation: "sur / autour de (postposition)",
        grammaticalCategory: "postposition",
        grammarRule: "KAN = postposition \"sur, dessus, autour de\". BOBARABA KAN = autour du grand bassin. Employe aussi au sens figure : \"au sujet de\".",
        example: "A be jigin so kan.",
        exampleTranslation: "Il descend du toit.",
      },
      ma: {
        translation: "à / pour (postposition dative)",
        grammaticalCategory: "postposition",
        grammarRule: "MA est la postposition dative indiquant le beneficiaire ou la destination. GALADONLA MA = a la teinturerie. Equivalente du complement d'objet indirect francais.",
        example: "A ye so di ne ma.",
        exampleTranslation: "Il m'a donne une maison.",
      },
      // ── Marqueurs grammaticaux ─────────────────────────────
      ti: {
        translation: "marqueur négatif (variante Wassoulou de TƐ)",
        grammaticalCategory: "marqueur",
        grammarRule: "TI est la variante negative Wassoulou de TƐ (marqueur negatif du progressif). Sujet + TI + Verbe = negation de l'action en cours ou habituelle. DEMISƐNW TI DƆN = les enfants ne connaissent pas.",
        example: "N ti dɔn.",
        exampleTranslation: "Je ne sais pas.",
      },
      min: {
        translation: "qui / que / dont (pronom relatif)",
        grammaticalCategory: "marqueur",
        grammarRule: "MIN est le pronom relatif en bambara, equivalent de \"qui, que, dont\". Introduit une proposition relative : WƐRƐWƐRƐ MIN DƆN = une excitation que les enfants ne connaissent pas. Invariable.",
        example: "Muso min ye na.",
        exampleTranslation: "La femme qui est venue.",
      },
      "dɔn": {
        translation: "savoir / connaître",
        grammaticalCategory: "verbe",
        grammarRule: "DƆN = connaitre, savoir. TI WƐRƐWƐRƐ MIN DƆN = ne pas connaitre cette excitation. A distinguer de DON (sans accent ouvert) = mettre, porter.",
        example: "N tɛ o dɔn.",
        exampleTranslation: "Je ne sais pas cela.",
      },
      bi: {
        translation: "maintenant / marqueur de futur proche",
        grammaticalCategory: "marqueur",
        grammarRule: "BI = aujourd'hui, maintenant ; comme marqueur grammatical : futur proche ou progressif immediat. N BI = \"je vais (bientot)\". A distinguer de BƐ (progressif habituel).",
        example: "N bi taa.",
        exampleTranslation: "Je pars maintenant.",
      },
      nba: {
        translation: "vraiment / bien sûr (particule affirmative)",
        grammaticalCategory: "marqueur",
        grammarRule: "NBA est une particule affirmative d'insistance : \"vraiment, bien sur, tout a fait\". Tres frequente dans la chanson Wassoulou pour exprimer conviction et celebration.",
        example: "O ka nyi nba.",
        exampleTranslation: "C'est vraiment bien.",
      },
      // ── Mots du marche ─────────────────────────────────────
      suguba: {
        translation: "grand marché",
        grammaticalCategory: "nom",
        grammarRule: "SUGUBA = grand marche (sugu = marche + ba = grand, suffixe augmentatif). Les grands marches d'Afrique de l'Ouest sont des lieux de rencontre sociale essentiels.",
        example: "N be taa suguba rɔ.",
        exampleTranslation: "Je vais au grand marche.",
      },
      "bamaɲari": {
        translation: "Bamagnari (lieu célèbre de commerce)",
        grammaticalCategory: "nom",
        grammarRule: "BAMAƝARI = Bamagnari, lieu celebre pour son marche en Afrique de l'Ouest. Reference geographique et culturelle symbolisant le commerce et la prosperite.",
        example: "Bamaɲari suguba rɔ.",
        exampleTranslation: "Au grand marche de Bamagnari.",
      },
      suguni: {
        translation: "petit marché",
        grammaticalCategory: "nom",
        grammarRule: "SUGUNI = petit marche (sugu = marche + ni = diminutif). Contrairement a SUGUBA (grand marche), SUGUNI est un marche de quartier, de proximite.",
        example: "N be taa suguni la.",
        exampleTranslation: "Je vais au petit marche.",
      },
      // ── Mots du tissu et de la mode ───────────────────────
      "bazɛn": {
        translation: "bazin (tissu damassé de prestige)",
        grammaticalCategory: "nom",
        grammarRule: "BAZƐN = tissu damasse precieux, symbole de prestige et d'elegance en Afrique de l'Ouest. Se teint a l'indigo ou en diverses couleurs. Porter du bazin signale le statut social.",
        example: "N ka bazɛn ka nyi.",
        exampleTranslation: "Mon bazin est beau.",
      },
      san: {
        translation: "acheter (ici) / ciel / an (homonymes)",
        grammaticalCategory: "verbe",
        grammarRule: "SAN = acheter (sens commercial dans ce contexte). BAZƐN SAN = acheter du bazin. A noter : SAN signifie aussi \"ciel\" et \"annee\" en bambara (homonymes distingues par le ton).",
        example: "N ye bazɛn san.",
        exampleTranslation: "J'ai achete du bazin.",
      },
      galadonla: {
        translation: "indigoterie / atelier de teinture",
        grammaticalCategory: "nom",
        grammarRule: "GALADONLA = atelier de teinture a l'indigo (indigoterie). GALADON = teinturier/teinturiere. Lieu central de l'artisanat textile en Afrique de l'Ouest.",
        example: "Ne be taa galadonla ma.",
        exampleTranslation: "Je vais a la teinturerie.",
      },
      "lɔmasa": {
        translation: "lumasa (technique de teinture traditionnelle)",
        grammaticalCategory: "nom",
        grammarRule: "LƆMASA = technique traditionnelle de teinture (type de batik). Art textile valorise dans la culture Wassoulou, transmis de generation en generation.",
        example: "I be lɔmasa ke wa?",
        exampleTranslation: "Tu fais du lumasa ?",
      },
      fiisa: {
        translation: "meilleur / couleur rose clair",
        grammaticalCategory: "adjectif",
        grammarRule: "FIISA = meilleur, superieur. Dans le contexte de la teinture, designe une couleur rose clair ou pastel tres prisee pour les tissus.",
        example: "O ka fiisa.",
        exampleTranslation: "C'est mieux.",
      },
      tayɛri: {
        translation: "tailleur",
        grammaticalCategory: "nom",
        grammarRule: "TAYƐRI = tailleur (emprunt du francais, integre au bambara). Artisan essentiel pour la confection des habits en bazin. La relation avec son tailleur est personnelle et de confiance.",
        example: "Tayɛri be n ka so.",
        exampleTranslation: "Le tailleur est dans ma maison.",
      },
      "tayɛrikɛ": {
        translation: "tailleur (praticien du métier)",
        grammaticalCategory: "nom",
        grammarRule: "TAYƐRIKƐ = celui/celle qui pratique le metier de tailleur (tayɛri + kɛ = faire). Designe le tailleur en tant que professionnel.",
        example: "N ka tayɛrikɛ ka baara ke.",
        exampleTranslation: "Mon tailleur travaille.",
      },
      kalan: {
        translation: "apprendre / faire confectionner",
        grammaticalCategory: "verbe",
        grammarRule: "KALAN = apprendre, lire ; dans le contexte artisanal Wassoulou : faire confectionner, faire coudre. BAZƐNNI KALAN = faire coudre le bazin.",
        example: "N be kalan.",
        exampleTranslation: "Je fais coudre.",
      },
      maniyɛri: {
        translation: "manière / style / façon de faire",
        grammaticalCategory: "nom",
        grammarRule: "MANIYƐRI = maniere, style, facon (emprunt du francais \"maniere\", pleinement integre en bambara). Designe le savoir-faire elegant d'un artisan.",
        example: "A ka maniyɛri ka nyi.",
        exampleTranslation: "Son style est beau.",
      },
      kanubagaluw: {
        translation: "fans / bien-aimés / admirateurs",
        grammaticalCategory: "nom",
        grammarRule: "KANUBAGALUW = fans, bien-aimes (kanu = amour + baga = celui qui aime + lu/w = pluriel). Designe les admirateurs d'un artiste. Oumou s'habille pour eux.",
        example: "N kanubagaluw be here.",
        exampleTranslation: "Mes fans sont en paix.",
      },
      // ── Mots geographiques et culturels ───────────────────
      wasolo: {
        translation: "Wassoulou (région historique du Mali)",
        grammaticalCategory: "nom",
        grammarRule: "WASOLO = Wassoulou, region du sud du Mali et de Guinee, berceau de la musique Wassoulou dont Oumou Sangare est l'ambassadrice mondiale. Symbole d'identite culturelle forte.",
        example: "Ne ye wasolo denmuso ye.",
        exampleTranslation: "Je suis une fille du Wassoulou.",
      },
      "diɲan": {
        translation: "monde / univers",
        grammaticalCategory: "nom",
        grammarRule: "DIƝAN = le monde, l'univers (emprunt de l'arabe \"dunya\" via le malinke). Oumou s'adresse au monde entier depuis son Wassoulou natal.",
        example: "Diɲan bɛɛ be na.",
        exampleTranslation: "Le monde entier vient.",
      },
      "jɔɔn": {
        translation: "personne / quelqu'un",
        grammaticalCategory: "nom",
        grammarRule: "JƆƆN = personne, quelqu'un. JƆƆN BƐƐ = chaque personne, tout le monde. (Historiquement : \"esclave\" en bambara classique, usage generalise au sens de \"personne\".",
        example: "Jɔɔn bɛɛ be n'i kanuba.",
        exampleTranslation: "Chaque personne a ses fans.",
      },
      kanuba: {
        translation: "fan / bien-aimé / amoureux",
        grammaticalCategory: "nom",
        grammarRule: "KANUBA = fan, bien-aime (kanu = amour + ba = grand, fort). Au pluriel : KANUBAGALUW. La relation artiste-fans est centrale dans la chanson Wassoulou.",
        example: "I ye n kanuba.",
        exampleTranslation: "Tu es mon fan.",
      },
      "madinɛkura": {
        translation: "Madina-la-Nouvelle (lieu de marché)",
        grammaticalCategory: "nom",
        grammarRule: "MADINƐKURA = Madina la nouvelle (madina = medina/ville en arabe + kura = nouveau). Lieu de marche en Afrique de l'Ouest, reference geographique de la tournee d'Oumou.",
        example: "N be taa Madinɛkura rɔ.",
        exampleTranslation: "Je vais a Madina-Nouvelle.",
      },
      // ── Corps, coiffure et artisanat ───────────────────────
      "cɛnin": {
        translation: "jeune homme / garçon",
        grammaticalCategory: "nom",
        grammarRule: "CƐNIN = jeune homme (cɛ = homme + ni = diminutif affectueux). L'excitation WƐRƐWƐRƐ habite le jeune homme qui assiste au spectacle.",
        example: "Cɛnin nin ka nyi.",
        exampleTranslation: "Ce jeune homme est bien.",
      },
      "demisɛnw": {
        translation: "enfants",
        grammaticalCategory: "nom",
        grammarRule: "DEMISƐNW = enfants au pluriel (variante Wassoulou de DENW). L'excitation de la chanson est si nouvelle que meme les enfants ne la connaissaient pas.",
        example: "Demisɛnw be wuli.",
        exampleTranslation: "Les enfants se levent.",
      },
      "cɛmisɛnw": {
        translation: "jeunes hommes / garçons",
        grammaticalCategory: "nom",
        grammarRule: "CƐMISƐNW = jeunes hommes (cɛ + misɛn = jeune + w = pluriel). L'agitation WUSƐWUSƐ que ces jeunes n'avaient jamais connue renforce l'intensite de la fete.",
        example: "Cɛmisɛnw be wuli.",
        exampleTranslation: "Les jeunes hommes se levent.",
      },
      kunnin: {
        translation: "cheveux / tête",
        grammaticalCategory: "nom",
        grammarRule: "KUNNIN = cheveux, tete (kun = tete + ni = diminutif affectueux). La coiffure est un element culturel important, symbole de soin de soi et de celebration.",
        example: "N ka kunnin ka nyi.",
        exampleTranslation: "Mes cheveux sont beaux.",
      },
      dan: {
        translation: "natter / tresser / terminer",
        grammaticalCategory: "verbe",
        grammarRule: "DAN = natter les cheveux, tresser ; aussi : terminer, fermer. KUNNIN DAN = natter les cheveux. La polysemie de DAN (finir/natter) exprime que la coiffure complete la mise.",
        example: "Ka kunnin dan.",
        exampleTranslation: "Natter les cheveux.",
      },
      siri: {
        translation: "attacher / nouer / passer autour",
        grammaticalCategory: "verbe",
        grammarRule: "SIRI = attacher, nouer, passer autour. BOBARABA KAN SIRI = nouer autour du grand bassin. Evoque les gestes de la coiffure et de la teinture traditionnelle.",
        example: "Ka a siri.",
        exampleTranslation: "L'attacher.",
      },
      bobaraba: {
        translation: "grand bassin / grande cuvette",
        grammaticalCategory: "nom",
        grammarRule: "BOBARABA = grand bassin, grande cuvette. Ustensile central de la teinture et de la coiffure traditionnelle. Symbolise le travail artisanal communautaire.",
        example: "Jii be bobaraba rɔ.",
        exampleTranslation: "L'eau est dans le grand bassin.",
      },
      sewire: {
        translation: "bienheureux / dans la joie (Wassoulou)",
        grammaticalCategory: "adjectif",
        grammarRule: "SEWIRE est une variante Wassoulou de SEWERE. Dans ce contexte festif, il prend le sens positif de \"bienheureux, dans une joie profonde\". Le sens depend du contexte culturel.",
        example: "Deni nin ka sewire.",
        exampleTranslation: "Cette petite est bienheureuse.",
      },
      // ── Mots existants conserves ───────────────────────────
      nawo: {
        translation: "venez ! (imperatif pluriel)",
        grammaticalCategory: "verbe",
        grammarRule: "NAWO = NA (venir) + WO (suffixe imperatif pluriel). Pour inviter plusieurs personnes a venir.",
        example: "Nawo yan!",
        exampleTranslation: "Venez ici !",
      },
      mali: {
        translation: "Mali (pays)",
        grammaticalCategory: "nom",
        grammarRule: "MALI est le nom du pays et aussi un mot bambara signifiant \"hippopotame\" — l'animal totem de l'Empire du Mali.",
        example: "Mali ka bon.",
        exampleTranslation: "Le Mali est grand.",
      },
      denw: {
        translation: "enfants / les enfants",
        grammaticalCategory: "nom",
        grammarRule: "DENW = DEN (enfant) + -W (suffixe du pluriel en bambara).",
        example: "Denw be wuli sini.",
        exampleTranslation: "Les enfants se leveront demain.",
      },
      wa: {
        translation: "particule d'invitation / d'enthousiasme / interrogative",
        grammaticalCategory: "marqueur",
        grammarRule: "WA est une particule polyvalente : (1) invitation et enthousiasme en fin de phrase ; (2) particule interrogative (\"n'est-ce pas?\"). Tres courante dans la chanson Wassoulou.",
        example: "An taa wa!",
        exampleTranslation: "Allons-y !",
      },
      ko: {
        translation: "dire / car / chose",
        grammaticalCategory: "connecteur",
        grammarRule: "KO est polyvalent : (1) verbe \"dire\" ; (2) conjonction \"car, parce que\" ; (3) nom \"chose\". Comme introducteur de discours, il introduit une assertion.",
        example: "A ko a be taa.",
        exampleTranslation: "Il a dit qu'il partait.",
      },
      deni: {
        translation: "enfant / petite (variante Wassoulou)",
        grammaticalCategory: "nom",
        grammarRule: "DENI est une variante de DEN (enfant), frequente dans les dialectes Wassoulou. Le -I final exprime l'affection.",
        example: "Deni nin ka nyi.",
        exampleTranslation: "Cette petite est bien.",
      },
      bena: {
        translation: "marqueur de futur (be + na)",
        grammaticalCategory: "marqueur",
        grammarRule: "BENA est la contraction de BE + NA, formant le marqueur de futur en bambara. Structure : Sujet + BENA + Verbe → action future.",
        example: "N bena taa sini.",
        exampleTranslation: "Je partirai demain.",
      },
    },
  },

  // ── Chanson 6 : Mani Djindala — Oumou Sangaré ────────────────────
  // Extrait educatif — traduction : Oumarou Yero Sidibe
  {
    id: "mani-djindala",
    title: "Mani Djindala",
    artist: "Oumou Sangare",
    genre: "Wassoulou",
    theme: "Solidarite des aventuriers et souffrance des pauvres",
    emoji: "🌍",
    difficulty: "avance",
    lines: [
      // ── Introduction ───────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "INTRODUCTION" },
      {
        words: ["Cɛdennu", "taara", "min", "?"],
        translation: "Où sont partis les braves hommes ?",
        structure: "CƐDENNU + TAARA (passé) + MIN (interrogatif locatif)",
        grammarNote: "CƐDENNU = les braves hommes (cɛ = homme + den = courageux + nu = pluriel Wassoulou). TAARA = est parti (passé de TAA = aller). MIN = où (interrogatif locatif).",
      },
      {
        words: ["Eh", "jula", "taara"],
        translation: "Oh ! les aventuriers sont partis",
        structure: "Exclamation EH + JULA + TAARA",
        grammarNote: "JULA = commerçant voyageur, aventurier. Terme noble désignant ceux qui partent loin chercher fortune. Figure centrale de la culture mandé. TAARA = passé de TAA.",
      },
      {
        words: ["Wasolo", "kɔnɔ", "kanbɔra"],
        translation: "L'artiste du Wassoulou s'est manifestée",
        structure: "WASOLO + KƆNƆ (artiste/oiseau) + KANBƆRA",
        grammarNote: "KƆNƆ = oiseau, métaphore de la voix artiste dans le Wassoulou. KANBƆRA = s'est manifestée (kan = faire entendre sa voix + bɔra = est sorti). Oumou se présente comme la voix du Wassoulou.",
      },
      {
        words: ["Munna", "ayi", "ma", "ne", "ta", "?"],
        translation: "Pourquoi vous ne m'avez pas amenée ?",
        structure: "MUNNA (pourquoi) + AYI + MA (négatif passé) + NE + TA",
        grammarNote: "MUNNA = pourquoi (dialecte Wassoulou, de MUN = quoi + NA = cause). AYI = vous (pronom 2e personne pluriel Wassoulou). MA = négatif du passé. TA = prendre, amener.",
      },
      {
        words: ["Ko", "juladennin", "walen", "min", "?"],
        translation: "Où est parti le jeune aventurier ?",
        structure: "KO + JULADENNIN + WALEN + MIN",
        grammarNote: "JULADENNIN = jeune aventurier (jula + den + nin diminutif). WALEN = où est-il allé ? (forme interrogative Wassoulou du passé de wale = aller quelque part).",
      },
      { words: ["N'ayi", "ma", "ne", "ta"], translation: "Si vous ne me prenez pas avec vous" },
      { words: ["Ko", "n'ayi", "ma", "ne", "ta"], translation: "Si vous ne me prenez pas avec vous" },
      {
        words: ["Ne", "na", "sɛn", "ni", "kurubataama"],
        translation: "Je me fatiguerai dans de longues marches",
        structure: "NE + NA (futur) + SɛN + NI + KURUBATAAMA",
        grammarNote: "SɛN = se fatiguer, marcher longuement. KURUBATAAMA = longues marches épuisantes, chemin de l'exil (terme poétique). La chanteuse préfère le voyage difficile à l'abandon.",
      },
      { words: ["Cɛ,", "n'ayi", "ma", "ne", "ta"], translation: "Hommes, si vous ne m'emmenez pas avec vous" },
      // ── Refrain — Mani Djindala ────────────────────────────────
      { words: [], translation: "", sectionTitle: "REFRAIN — MANI DJINDALA" },
      {
        words: ["Nin", "ye", "mani", "jindala", "ye"],
        translation: "C'est... jindala",
        structure: "NIN YE ... YE (structure prédicative) + MANI + JINDALA",
        grammarNote: "NIN = ceci. YE ... YE = structure prédicative bambara (\"c'est\"). MANI = particule poétique d'intensité. JINDALA = terme Wassoulou ineffable : état de l'âme, mélancolie douce, beauté mystérieuse.",
      },
      {
        words: ["Barokɛɲɔgɔn,", "nin", "ye", "mani", "jindala"],
        translation: "Compagnon de bénédiction, c'est... jindala",
        structure: "BAROKƐƝƆGƆN (apostrophe) + NIN YE MANI JINDALA",
        grammarNote: "BAROKƐƝƆGƆN = compagnon de bénédiction (baro = offrande/bénédiction + kɛ = faire + ɲɔgɔn = compagnon). Désigne l'ami fidèle des moments de grâce.",
      },
      { words: ["Bangali", "feere"], translation: "Bangali feere (cri d'appel traditionnel Wassoulou)" },
      {
        words: ["Eh", "n", "sumukɛɲɔgɔn,", "nin", "ye", "mani", "jindala"],
        translation: "Oh mon compagnon de tristesse, c'est... jindala",
        structure: "EH + N SUMUKƐƝƆGƆN (apostrophe) + NIN YE MANI JINDALA",
        grammarNote: "SUMUKƐƝƆGƆN = compagnon de tristesse (sumu = mélancolie + kɛ + ɲɔgɔn). Contrepoint au BAROKƐƝƆGƆN : l'ami des moments difficiles. La chanson embrasse joie ET peine.",
      },
      // ── Joie partagée ─────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "JOIE PARTAGÉE" },
      {
        words: ["I", "tɛ", "sawa,", "ne", "ka", "n", "sawa", "i", "fɛ"],
        translation: "Réjouis-toi afin que je me réjouisse avec toi",
        structure: "I TƐ SAWA (invitation) + NE KA N SAWA + I FƐ (avec toi)",
        grammarNote: "TƐ SAWA = réjouis-toi (tɛ = impératif négatif inversé en Wassoulou / subjonctif). KA = pour que. SAWA = se réjouir. FƐ = postposition 'avec, auprès de'.",
      },
      { words: ["I", "t'i", "sawa,", "ne", "ka", "n", "sawa", "i", "ye"], translation: "Réjouis-toi afin que je me réjouisse avec toi" },
      {
        words: ["I", "t'i", "yɛlɛ,", "ne", "ka", "n", "yɛlɛ", "i", "fɛ"],
        translation: "Souris afin que je sourie avec toi",
        structure: "I T'I YƐLƐ + NE KA N YƐLƐ + I FƐ",
        grammarNote: "YƐLƐ = sourire, rire. La structure parallèle SAWA → YƐLƐ → JUGU (joie → sourire → colère) démontre une solidarité totale : partager toutes les émotions.",
      },
      {
        words: ["Sunguru", "rɔ", "mɔgɔ", "mandi"],
        translation: "La plus aimée des jeunes filles",
        structure: "SUNGURU RƆ MƆGƆ MANDI (superlatif affectif)",
        grammarNote: "SUNGURU = jeune fille. RƆ = parmi (postposition). MƆGƆ = personne. MANDI = bien-aimé(e) (contraction de MAN DI = 'est bien'). Structure superlative très poétique.",
      },
      {
        words: ["Kanbele", "rɔ", "mɔgɔ", "mandi"],
        translation: "Le plus aimé des jeunes hommes",
        grammarNote: "KANBELE = jeune homme (kan = voix/jeunesse + bele = beau). La paire SUNGURU/KANBELE incarne la jeunesse. Oumou s'adresse à toute la jeunesse avec la même tendresse.",
      },
      // ── Solidarité ────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "SOLIDARITÉ" },
      {
        words: ["K'i", "man'i", "jugu,", "ne", "bɛ", "jugu", "i", "fɛ"],
        translation: "Si tu te fâches, je me fâcherai avec toi",
        structure: "K'I MAN'I JUGU (conditionnel) + NE BƐ JUGU I FƐ",
        grammarNote: "MAN'I = contraction de 'mana i' (si tu). JUGU = se fâcher, être en colère. BƐ = progressif (ici futur). La solidarité va jusqu'à partager la colère : un engagement total.",
      },
      { words: ["Eh", "n", "bɛrɛninɲɔgɔn,", "i", "man'i", "jugu", "ne", "bɛ", "jugu", "i", "ye"], translation: "Oh mon compagnon du cœur, si tu te fâches, je me fâcherai avec toi" },
      { words: ["I", "man'i", "sawa,", "ne", "bɛ", "n", "sawa", "i", "fɛ"], translation: "Si tu te réjouis, je me réjouirai avec toi" },
      { words: ["Kanbele", "rɔ", "mɔgɔ", "man", "di"], translation: "Le plus aimé des jeunes hommes" },
      // ── Faire son choix ────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "FAIRE SON CHOIX" },
      {
        words: ["K'i", "y'i", "ɲanata", "ta", "sunguruyi", "rɔ"],
        translation: "Fais ton choix parmi les jeunes filles",
        structure: "K'I Y'I ƝANATA TA + SUNGURUYI RƆ",
        grammarNote: "ƝANATA = choix, préférence (ɲana = désirer + ta = prendre). SUNGURUYI = les jeunes filles (pluriel élégant en -yi). K'I Y'I = pour que tu... L'invitation est respectueuse du libre-arbitre.",
      },
      { words: ["I", "y'i", "ɲanata", "ta", "kanbeleyi", "rɔ"], translation: "Fais ton choix parmi les jeunes hommes" },
      {
        words: ["I", "t'i", "ɲanata", "ta", "duniɲa"],
        translation: "Fais ton choix dans la vie",
        structure: "I T'I ƝANATA TA + DUNIƝA",
        grammarNote: "DUNIƝA = le monde, la vie (de l'arabe 'dunya'). L'invitation s'élargit : de la jeunesse (sunguru/kanbele) à toute la vie. Un appel à saisir ses chances.",
      },
      // ── Contemplation ──────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "CONTEMPLATION" },
      { words: ["Ne", "ma", "maa", "ye", "si", "kɔnɔ", "ye"], translation: "Je n'ai jamais vu personne comme cela" },
      { words: ["Eh", "n", "diyanyemɔgɔ"], translation: "Oh mon bien-aimé" },
      { words: ["Eh", "n", "mɛrɛninɲɔgɔn"], translation: "Oh mon compagnon du cœur" },
      { words: ["Ja,", "ne", "maa", "ye", "jindala", "Ala", "ye"], translation: "Regarde, j'ai vu le jindala par Dieu" },
      { words: ["Ja,", "ne", "m'a", "ye", "jindala"], translation: "Regarde, je ne l'ai pas vu, le jindala" },
      { words: ["Ne", "ma", "n", "maa", "ye", "gwaso", "rɔ", "ye"], translation: "Je n'ai pas vu quelqu'un comme moi en ce monde" },
      // ── Refrain (reprise) ──────────────────────────────────────
      { words: [], translation: "", sectionTitle: "REFRAIN — MANI DJINDALA" },
      { words: ["Nin", "ye", "mani", "jindala", "ye"], translation: "C'est... jindala" },
      { words: ["Barokɛɲɔgɔn,", "nin", "ye", "mani", "jindala"], translation: "Compagnon de bénédiction, c'est... jindala" },
      // ── L'artiste ──────────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "L'ARTISTE" },
      { words: ["Sunguruyi", "rɔ", "mɔgɔ", "man", "di"], translation: "La plus aimée des jeunes filles" },
      {
        words: ["Kanbele", "rɔ", "lagarenin"],
        translation: "Le benjamin des jeunes hommes",
        structure: "KANBELE RƆ LAGARENIN",
        grammarNote: "LAGARENIN = le benjamin, le cadet (laga = rang + re + nin = diminutif affectueux). Le plus jeune est souvent le plus chéri. RƆ = parmi.",
      },
      {
        words: ["N", "b'a", "lɔn,", "ayi", "ni", "tɔgɔma"],
        translation: "Je sais que vous avez fait de longs voyages",
        structure: "N B'A LƆN + AYI NI TƆGƆMA",
        grammarNote: "B'A LƆN = bɛ a lɔn = je le sais. LƆN = savoir, connaître. TƆGƆMA = ceux qui ont fait un long chemin, voyageurs de longue haleine (tɔgɔ = chemin/renommée + ma = qui a).",
      },
      { words: ["k'ayi", "m'a", "lɔn", "kɔnɔ", "kanbɔra"], translation: "Ne savez-vous pas que l'artiste s'est manifestée ?" },
      {
        words: ["Ayi", "m'a", "lɔn", "Umu", "kanbɔra"],
        translation: "Ne savez-vous pas que Oumou s'est manifestée ?",
        structure: "AYI M'A LƆN + NOM + KANBƆRA (question rhétorique)",
        grammarNote: "AYI M'A LƆN = ne savez-vous pas ? (rhétorique affirmatif). UMU = Oumou, forme courte du prénom Oumarou. Oumou se nomme elle-même comme la voix qui s'est levée.",
      },
      {
        words: ["Ko", "yarasulu", "mantɔnnu"],
        translation: "Je dis, chers partisans du prophète de Dieu",
        structure: "KO (discours) + YARASULU + MANTƆNNU (apostrophe affectueuse)",
        grammarNote: "YARASULU = partisans du prophète (ya = cher + rasulu = prophète/messager, de l'arabe rasul). MANTƆNNU = chers, estimés (terme affectueux Wassoulou).",
      },
      {
        words: ["Sangare", "kɔnɔ", "k'ayi", "ni", "ɲaani"],
        translation: "L'artiste Sangaré vous témoigne sa compassion",
        structure: "SANGARE KƆNƆ + K'AYI NI ƝAANI",
        grammarNote: "ƝAANI = compassion profonde, sympathie pour la souffrance d'autrui. K'AYI NI ƝAANI = que la compassion soit avec vous. Formule de solidarité spirituelle forte dans la tradition Wassoulou.",
      },
      // ── La souffrance des pauvres ──────────────────────────────
      { words: [], translation: "", sectionTitle: "LA SOUFFRANCE DES PAUVRES" },
      {
        words: ["Fantan", "ni", "dimi", "lo"],
        translation: "Le pauvre a mal",
        structure: "FANTAN + NI + DIMI + LO (particule affirmative)",
        grammarNote: "FANTAN = pauvre, indigent (fan = vide/sans + tan = qui porte). DIMI = douleur. LO = particule affirmative conclusive Wassoulou (\"c'est ainsi, il en est ainsi\").",
      },
      { words: ["Fantan", "ni", "ɲani", "lo"], translation: "Le pauvre souffre",
        grammarNote: "ƝANI = souffrir intérieurement (plus profond que DIMI = douleur physique). La répétition DIMI / ƝANI couvre toutes les formes de souffrance.",
      },
      {
        words: ["Fanta", "ni", "tɔɔrɔya", "siyaman"],
        translation: "Le pauvre vit dans beaucoup de difficultés",
        structure: "FANTA + NI + TƆƆRƆYA + SIYAMAN",
        grammarNote: "TƆƆRƆYA = difficultés, tribulations (nom de tɔɔrɔ = souffrir). SIYAMAN = beaucoup, nombreux. Une vie entière de labeur et d'épreuves.",
      },
      {
        words: ["E", "m'a", "lɔn", "dimi", "tɛ", "banna"],
        translation: "Ne savez-vous pas que le mal ne finit pas",
        structure: "E M'A LƆN + DIMI + TƐ BANNA",
        grammarNote: "TƐ BANNA = ne finit pas (tɛ = négation + banna = finir). BANNA = finir, s'arrêter. La souffrance sans fin : constat implacable que seule une voix comme Oumou ose chanter.",
      },
      { words: ["Cɛ,", "i", "m'a", "lɔn", "ɲani", "tɛ", "banna"], translation: "Homme, ne sais-tu pas que la souffrance ne finit pas" },
      { words: ["k'ayi", "m'a", "lɔn", "dimi", "tɛ", "banna", "Ala"], translation: "Dieu ! Ne savez-vous pas que la peine ne finit pas" },
      {
        words: ["Cɛdennu", "tɔɔrɔlen", "don"],
        translation: "Les braves hommes souffrent",
        structure: "CƐDENNU + TƆƆRƆLEN (participe) + DON",
        grammarNote: "TƆƆRƆLEN = souffrant, qui souffre (participe actif : tɔɔrɔ + len). DON = copule affirmative (\"c'est\"). Cette formule-bilan conclut chaque lamentation.",
      },
      // ── Les aventuriers ────────────────────────────────────────
      { words: [], translation: "", sectionTitle: "LES AVENTURIERS" },
      {
        words: ["Gabɔn", "jula,", "k'ayi", "ni", "dimi"],
        translation: "Les aventuriers au Gabon, compassion à votre peine",
        structure: "Pays + JULA + K'AYI NI DIMI (formule de compassion)",
        grammarNote: "La litanie géographique (Gabon → Côte d'Ivoire → Libye → France) est une prière pour tous les Maliens de la diaspora. K'AYI NI X = que X soit avec vous.",
      },
      { words: ["Kodowari", "jula,", "k'ayi", "ni", "tɔɔrɔ"], translation: "Les aventuriers en Côte d'Ivoire, compassion à votre souffrance" },
      { words: ["Libi", "jula", "k'ayi", "ni", "ɲaani"], translation: "Les aventuriers en Libye, compassion à votre souffrance" },
      { words: ["Faransi", "jula,", "k'ayi", "ni", "dimi"], translation: "Les aventuriers en France, compassion à votre peine" },
      {
        words: ["Cɛdenyi", "ni", "dimi", "don"],
        translation: "Les braves hommes ont mal",
        grammarNote: "CƐDENYI = variante de CƐDENNU. NI DIMI DON = ont mal (ni = et/avec + dimi = douleur + don = c'est). La litanie s'achève sur la réalité universelle.",
      },
      { words: ["Cɛdennu", "tɔɔrɔlen", "don"], translation: "Les braves hommes souffrent" },
    ],
    wordAnnotations: {
      ...COMMON_ANNOTATIONS,
      // ── Protagonistes ──────────────────────────────────────────
      "cɛdennu": {
        translation: "les braves hommes",
        grammaticalCategory: "nom",
        grammarRule: "CƐDENNU = les braves hommes (cɛ = homme + den = courageux/fils + nu = pluriel Wassoulou). Désigne les hommes vaillants partis à l'aventure loin de leur terre natale.",
        example: "Cɛdennu taara.",
        exampleTranslation: "Les braves hommes sont partis.",
      },
      jula: {
        translation: "commerçant voyageur / aventurier",
        grammaticalCategory: "nom",
        grammarRule: "JULA = commerçant voyageur, aventurier. Terme noble en bambara/dioula pour ceux qui partent chercher fortune au loin. Figure centrale de la culture mandé, respectée pour son courage.",
        example: "Jula taara kɔrɔ.",
        exampleTranslation: "L'aventurier est parti au loin.",
      },
      "kɔnɔ": {
        translation: "oiseau / artiste / intérieur (polysémique)",
        grammaticalCategory: "nom",
        grammarRule: "KƆNƆ est polysémique : (1) oiseau — métaphore de la voix libre. (2) artiste, chanteur dans les traditions Wassoulou. (3) intérieur, cœur. Ici : l'artiste Oumou est 'l'oiseau du Wassoulou'.",
        example: "Wasolo kɔnɔ kanbɔra.",
        exampleTranslation: "L'artiste du Wassoulou s'est manifestée.",
      },
      kanbɔra: {
        translation: "s'est manifesté(e) / a fait entendre sa voix",
        grammaticalCategory: "verbe",
        grammarRule: "KANBƆRA = s'est manifesté(e) (kan = faire entendre sa voix + bɔra = est sorti, passé de bɔ). Évoque l'artiste qui se révèle au monde, qui fait entendre sa voix.",
        example: "Kɔnɔ kanbɔra.",
        exampleTranslation: "L'artiste s'est manifestée.",
      },
      ayi: {
        translation: "vous (pronom pluriel Wassoulou)",
        grammaticalCategory: "pronom",
        grammarRule: "AYI = vous (2e personne pluriel, dialecte Wassoulou). Équivalent de AWO ou OLU en bambara standard. Très fréquent chez Oumou Sangaré pour s'adresser collectivement au public.",
        example: "Munna ayi ma ne ta ?",
        exampleTranslation: "Pourquoi vous ne m'avez pas amenée ?",
      },
      munna: {
        translation: "pourquoi",
        grammaticalCategory: "marqueur",
        grammarRule: "MUNNA = pourquoi (interrogatif de cause, Wassoulou). Contraction de MUN NA (mun = quoi + na = cause/venir). Équivalent de MUN KAMA en bambara standard.",
        example: "Munna i taara ?",
        exampleTranslation: "Pourquoi es-tu parti ?",
      },
      taara: {
        translation: "est parti / s'en est allé (passé)",
        grammaticalCategory: "verbe",
        grammarRule: "TAARA = passé de TAA (aller). Le passé bambara se forme avec -RA/-LA. TAARA = est allé, est parti. Verbe de mouvement central dans cette chanson de l'exil et du voyage.",
        example: "A taara sini.",
        exampleTranslation: "Il est parti hier.",
      },
      ta: {
        translation: "prendre / amener / emmener",
        grammaticalCategory: "verbe",
        grammarRule: "TA = prendre, amener avec soi. NE TA = m'amener. AYI MA NE TA = vous ne m'avez pas amenée. Exprime l'exclusion douloureuse de la chanteuse des aventures collectives.",
        example: "A y'a ta.",
        exampleTranslation: "Il l'a pris.",
      },
      // ── Vocabulaire du refrain ──────────────────────────────────
      jindala: {
        translation: "jindala (terme poétique — état de l'âme)",
        grammaticalCategory: "nom",
        grammarRule: "JINDALA est un terme poétique Wassoulou évoquant un état ineffable : mélancolie douce, beauté mystérieuse, destin inéluctable. Il résiste à toute traduction littérale — c'est l'âme de la chanson.",
        example: "Nin ye mani jindala ye.",
        exampleTranslation: "C'est... jindala.",
      },
      mani: {
        translation: "tellement / quelque chose de... (particule poétique)",
        grammaticalCategory: "marqueur",
        grammarRule: "MANI = particule poétique exprimant l'intensité ou l'indicible. MANI JINDALA = quelque chose d'aussi profond que le jindala, un état au-delà des mots.",
        example: "Nin ye mani nyi.",
        exampleTranslation: "C'est tellement beau.",
      },
      "barokɛɲɔgɔn": {
        translation: "compagnon de bénédiction",
        grammaticalCategory: "nom",
        grammarRule: "BAROKƐƝƆGƆN = compagnon de bénédiction (baro = bénédiction/offrande + kɛ = faire + ɲɔgɔn = compagnon). L'ami fidèle des moments de grâce et de célébration.",
        example: "N barokɛɲɔgɔn ye na.",
        exampleTranslation: "Mon compagnon de bénédiction est venu.",
      },
      "sumukɛɲɔgɔn": {
        translation: "compagnon de tristesse",
        grammaticalCategory: "nom",
        grammarRule: "SUMUKƐƝƆGƆN = compagnon de tristesse (sumu = mélancolie + kɛ + ɲɔgɔn). Contrepoint du BAROKƐƝƆGƆN : l'ami des moments difficiles. La chanson honore les deux.",
        example: "N sumukɛɲɔgɔn be n fɛ.",
        exampleTranslation: "Mon compagnon de tristesse est avec moi.",
      },
      // ── Émotions partagées ─────────────────────────────────────
      sawa: {
        translation: "se réjouir / être heureux",
        grammaticalCategory: "verbe",
        grammarRule: "SAWA = se réjouir, être dans la joie. I TƐ SAWA = réjouis-toi (invitation). NE KA N SAWA = pour que je me réjouisse. Structure de solidarité émotionnelle.",
        example: "A sawa kosɛbɛ.",
        exampleTranslation: "Il s'est beaucoup réjoui.",
      },
      "yɛlɛ": {
        translation: "sourire / rire",
        grammaticalCategory: "verbe",
        grammarRule: "YƐLƐ = sourire, rire. La triade SAWA (joie) → YƐLƐ (sourire) → JUGU (colère) dans la chanson illustre une solidarité totale : partager toutes les émotions sans exception.",
        example: "A yɛlɛla.",
        exampleTranslation: "Il a souri.",
      },
      jugu: {
        translation: "se fâcher / ennemi",
        grammaticalCategory: "verbe",
        grammarRule: "JUGU = (1) se fâcher, être en colère (verbe). (2) ennemi, mauvais (nom/adj). K'I MAN'I JUGU = si tu te fâches. Promettre de partager même la colère est un engagement de solidarité extrême.",
        example: "A ma jugu.",
        exampleTranslation: "Il ne s'est pas fâché.",
      },
      // ── Vocabulaire social ─────────────────────────────────────
      sunguru: {
        translation: "jeune fille / demoiselle",
        grammaticalCategory: "nom",
        grammarRule: "SUNGURU = jeune fille. SUNGURUYI = les jeunes filles (pluriel en -yi). Terme affectueux et respectueux dans la tradition Wassoulou. Oumou chante la jeunesse féminine avec fierté.",
        example: "Sunguru nin ka nyi.",
        exampleTranslation: "Cette jeune fille est belle.",
      },
      kanbele: {
        translation: "jeune homme",
        grammaticalCategory: "nom",
        grammarRule: "KANBELE = jeune homme (kan + bele = beau/jeune). KANBELEYI = les jeunes hommes. La paire SUNGURU / KANBELE incarne toute la jeunesse dans les chansons Wassoulou.",
        example: "Kanbele nin be taa.",
        exampleTranslation: "Ce jeune homme va partir.",
      },
      "mɔgɔ": {
        translation: "personne / quelqu'un / gens",
        grammaticalCategory: "nom",
        grammarRule: "MƆGƆ = personne, être humain. MƆGƆ MANDI = la personne la plus aimée parmi... Structure superlative : GROUPE + RƆ + MƆGƆ + MANDI.",
        example: "Mɔgɔ bɛɛ be na.",
        exampleTranslation: "Tout le monde vient.",
      },
      mandi: {
        translation: "bien-aimé(e) / le plus aimé",
        grammaticalCategory: "adjectif",
        grammarRule: "MANDI = contraction de MAN DI (\"est bien/bon\"). Superlatif affectif : MƆGƆ MANDI = la personne la plus chérie. Très courant dans la poésie Wassoulou pour exprimer l'affection maximale.",
        example: "A ka mandi.",
        exampleTranslation: "Il est bien-aimé.",
      },
      "ɲanata": {
        translation: "choix / préférence",
        grammaticalCategory: "nom",
        grammarRule: "ƝANATA = choix, ce qu'on désire prendre (ɲana = désirer + ta = prendre). I Y'I ƝANATA TA = fais ton choix. Invite chacun à exercer son libre-arbitre.",
        example: "I y'i ɲanata ta.",
        exampleTranslation: "Fais ton choix.",
      },
      // ── Souffrance et compassion ───────────────────────────────
      fantan: {
        translation: "pauvre / indigent",
        grammaticalCategory: "nom",
        grammarRule: "FANTAN = pauvre (fan = vide/sans + tan = qui porte). Oumou, fille du peuple, chante la réalité des pauvres avec une empathie directe et sans détour.",
        example: "Fantan be dimi.",
        exampleTranslation: "Le pauvre souffre.",
      },
      dimi: {
        translation: "douleur / mal / souffrance",
        grammaticalCategory: "nom",
        grammarRule: "DIMI = douleur, mal (physique et moral). K'AYI NI DIMI = compassion à votre douleur (formule de solidarité). DIMI TƐ BANNA = la douleur ne finit pas.",
        example: "Dimi be ne na.",
        exampleTranslation: "J'ai mal.",
      },
      "ɲani": {
        translation: "souffrir / souffrance intérieure",
        grammaticalCategory: "verbe",
        grammarRule: "ƝANI = souffrir intérieurement (plus profond que DIMI = douleur physique). ƝANI TƐ BANNA = la souffrance ne finit pas. Le chant social Wassoulou nomme toutes les formes de peine.",
        example: "A ɲanilen don.",
        exampleTranslation: "Il souffre.",
      },
      "tɔɔrɔya": {
        translation: "difficultés / tribulations / souffrance",
        grammaticalCategory: "nom",
        grammarRule: "TƆƆRƆYA = difficultés (nom dérivé de tɔɔrɔ = souffrir). TƆƆRƆYA SIYAMAN = beaucoup de difficultés. Désigne l'ensemble des épreuves de la vie du migrant.",
        example: "Tɔɔrɔya be a nɔ.",
        exampleTranslation: "Il a des difficultés.",
      },
      siyaman: {
        translation: "beaucoup / nombreux",
        grammaticalCategory: "adjectif",
        grammarRule: "SIYAMAN = beaucoup, nombreux (siya = augmenter + man). Quantificateur d'intensité très courant en bambara et dioula.",
        example: "Mɔgɔ siyaman taara.",
        exampleTranslation: "Beaucoup de personnes sont parties.",
      },
      banna: {
        translation: "finir / cesser / s'arrêter",
        grammaticalCategory: "verbe",
        grammarRule: "BANNA = finir, s'arrêter (passé de BAN). TƐ BANNA = ne finit pas. L'image de la souffrance sans fin est le cœur du message social d'Oumou : personne ne veut regarder la réalité en face.",
        example: "Baara banna.",
        exampleTranslation: "Le travail est terminé.",
      },
      "tɔɔrɔlen": {
        translation: "souffrant / qui souffre (participe)",
        grammaticalCategory: "adjectif",
        grammarRule: "TƆƆRƆLEN = participe actif de tɔɔrɔ + len (suffixe participial). CƐDENNU TƆƆRƆLEN DON = les braves hommes souffrent. Formule-refrain conclusive de chaque lamentation.",
        example: "Mɔgɔ tɔɔrɔlen ye na.",
        exampleTranslation: "La personne qui souffre est venue.",
      },
      lo: {
        translation: "c'est ainsi / particule affirmative conclusive",
        grammaticalCategory: "marqueur",
        grammarRule: "LO = particule affirmative conclusive du dialecte Wassoulou (équivalent de YE DON en bambara standard). DIMI LO = il a mal (c'est ainsi). Martèle une vérité comme un constat sans appel.",
        example: "O lo.",
        exampleTranslation: "C'est ainsi.",
      },
      "lɔn": {
        translation: "savoir / connaître",
        grammaticalCategory: "verbe",
        grammarRule: "LƆN = savoir, connaître (synonyme de DƆN). M'A LƆN = ne sait pas (ma = négatif passé + a = le/la). AYI M'A LƆN = ne savez-vous pas ? (question rhétorique d'affirmation).",
        example: "N b'a lɔn.",
        exampleTranslation: "Je le sais.",
      },
      "ɲaani": {
        translation: "compassion / sympathie profonde",
        grammaticalCategory: "nom",
        grammarRule: "ƝAANI = compassion profonde, sympathie pour la souffrance d'autrui. K'AYI NI ƝAANI = que la compassion soit avec vous. Formule de solidarité spirituelle très forte dans la tradition Wassoulou.",
        example: "N'ayi ni ɲaani.",
        exampleTranslation: "Compassion à vous.",
      },
      // ── Géographie de la diaspora ──────────────────────────────
      "gabɔn": {
        translation: "Gabon",
        grammaticalCategory: "nom",
        grammarRule: "GABƆN = Gabon, pays d'Afrique centrale. Destination importante de la migration malienne. La litanie géographique couvre toute la diaspora avec une égale compassion.",
        example: "Gabɔn jula taara.",
        exampleTranslation: "Les aventuriers sont partis au Gabon.",
      },
      kodowari: {
        translation: "Côte d'Ivoire",
        grammaticalCategory: "nom",
        grammarRule: "KODOWARI = Côte d'Ivoire (transcription phonétique bambara). Pays voisin du Mali et principale destination des migrants maliens en Afrique de l'Ouest.",
        example: "A taara Kodowari.",
        exampleTranslation: "Il est parti en Côte d'Ivoire.",
      },
      libi: {
        translation: "Libye",
        grammaticalCategory: "nom",
        grammarRule: "LIBI = Libye. Pays de transit sur la route migratoire vers l'Europe, particulièrement dangereux. La compassion d'Oumou pour les LIBI JULA est d'une actualité brûlante.",
        example: "Libi jula ni tɔɔrɔ.",
        exampleTranslation: "Compassion aux aventuriers en Libye.",
      },
      faransi: {
        translation: "France",
        grammaticalCategory: "nom",
        grammarRule: "FARANSI = France (emprunt phonétique). Ancienne puissance coloniale et destination principale de la diaspora malienne en Europe. Les FARANSI JULA sont les Maliens immigrés en France.",
        example: "A taara Faransi.",
        exampleTranslation: "Il est parti en France.",
      },
      // ── Autres mots clés ───────────────────────────────────────
      "sɛn": {
        translation: "se fatiguer / marcher longuement",
        grammaticalCategory: "verbe",
        grammarRule: "SɛN = se fatiguer, marcher longuement dans l'épreuve. NE NA SɛN NI KURUBATAAMA = je me fatiguerai dans de longues marches. Évoque l'errance douloureuse de l'exilé.",
        example: "N sɛnlen don.",
        exampleTranslation: "Je suis épuisé(e).",
      },
      kurubataama: {
        translation: "longues marches épuisantes",
        grammaticalCategory: "nom",
        grammarRule: "KURUBATAAMA = marches épuisantes, chemin difficile de l'exil (kuru = difficultés + bataama = marcher péniblement). Terme poétique pour l'errance migratoire.",
        example: "Kurubataama be n faamu.",
        exampleTranslation: "Les longues marches m'épuisent.",
      },
      lagarenin: {
        translation: "benjamin / cadet",
        grammaticalCategory: "nom",
        grammarRule: "LAGARENIN = le benjamin, le cadet (laga = rang + re + nin = diminutif). KANBELE RƆ LAGARENIN = le cadet parmi les jeunes hommes, souvent le plus chéri et le plus protégé.",
        example: "Lagarenin ye na.",
        exampleTranslation: "Le benjamin est venu.",
      },
      "bɛrɛninɲɔgɔn": {
        translation: "compagnon intime / compagnon du cœur",
        grammaticalCategory: "nom",
        grammarRule: "BɛRɛNINɲɔGɔN = compagnon intime (bɛrɛ = proche + nin = diminutif + ɲɔgɔn = compagnon). L'ami le plus cher, avec qui on partage tout, joie et peine.",
        example: "N bɛrɛninɲɔgɔn be ne fɛ.",
        exampleTranslation: "Mon compagnon du cœur est près de moi.",
      },
      "cɛdenyi": {
        translation: "les braves hommes (variante plurielle)",
        grammaticalCategory: "nom",
        grammarRule: "CƐDENYI = variante de CƐDENNU. YI et NU sont deux suffixes pluriels différents du dialecte Wassoulou. Même sens : les hommes courageux partis à l'aventure.",
        example: "Cɛdenyi ni dimi don.",
        exampleTranslation: "Les braves hommes ont mal.",
      },
    },
  },
];

// ─── Structures grammaticales fondamentales du bambara ────────────
export interface GrammarStructure {
  id: string;
  pattern: string;
  label: string;
  description: string;
  color: string;
  emoji: string;
  examples: Array<{ bambara: string; french: string; highlight: number[] }>;
}

export const BAMBARA_GRAMMAR_STRUCTURES: GrammarStructure[] = [
  {
    id: "accompli",
    pattern: "Sujet + YE + Verbe",
    label: "Accompli passe",
    description: "Action terminee dans le passe. Le marqueur YE se place entre le sujet et le verbe.",
    color: "#c9a961",
    emoji: "✅",
    examples: [
      { bambara: "N ye taa", french: "Je suis parti", highlight: [1] },
      { bambara: "A ye dun", french: "Il/Elle a mange", highlight: [1] },
      { bambara: "U ye kuma", french: "Ils ont parle", highlight: [1] },
    ],
  },
  {
    id: "inaccompli",
    pattern: "Sujet + BE + Verbe",
    label: "Present / Inaccompli",
    description: "Action en cours, habituelle ou future proche. BE exprime l'inaccompli.",
    color: "#2e7d32",
    emoji: "▶️",
    examples: [
      { bambara: "N be taa", french: "Je pars / Je suis en train de partir", highlight: [1] },
      { bambara: "A be kuma", french: "Il/Elle parle", highlight: [1] },
      { bambara: "U be baara ke", french: "Ils travaillent", highlight: [1] },
    ],
  },
  {
    id: "negatif",
    pattern: "Sujet + TE + Verbe",
    label: "Negation au present",
    description: "Negation de l'inaccompli. TE remplace BE pour nier une action au present.",
    color: "#c62828",
    emoji: "❌",
    examples: [
      { bambara: "N te taa", french: "Je ne pars pas", highlight: [1] },
      { bambara: "A te kuma", french: "Il/Elle ne parle pas", highlight: [1] },
      { bambara: "U te dun", french: "Ils ne mangent pas", highlight: [1] },
    ],
  },
  {
    id: "attributif",
    pattern: "Sujet + Attribut + YE",
    label: "Attributif — \"est\"",
    description: "Pour dire ce qu'est quelqu'un/quelque chose. YE se place en FIN de groupe.",
    color: "#6b3fa0",
    emoji: "🏷️",
    examples: [
      { bambara: "Ne ce ye", french: "Je suis un homme", highlight: [2] },
      { bambara: "A muso ye", french: "Elle est une femme", highlight: [2] },
      { bambara: "O kono ye", french: "C'est un oiseau", highlight: [2] },
    ],
  },
  {
    id: "qualite",
    pattern: "Sujet + KA + Adjectif",
    label: "Qualite permanente",
    description: "KA exprime une qualite stable, permanente ou intrinseque.",
    color: "#e65100",
    emoji: "💎",
    examples: [
      { bambara: "Duniya ka nyi", french: "La vie est belle (par essence)", highlight: [1] },
      { bambara: "So ka bon", french: "La maison est grande", highlight: [1] },
      { bambara: "Baara ka ni", french: "Le travail est bon", highlight: [1] },
    ],
  },
  {
    id: "postposition",
    pattern: "Nom + Postposition",
    label: "Complement circonstanciel",
    description: "En bambara, les prepositions sont postposees (apres le nom). Principales : LA, KONO, KO, FE.",
    color: "#0277bd",
    emoji: "📍",
    examples: [
      { bambara: "So kono", french: "Dans la maison (so = maison, kono = dans)", highlight: [1] },
      { bambara: "Sugu la", french: "Au marche (sugu = marche, la = a/au)", highlight: [1] },
      { bambara: "Jiri fe", french: "Pres de l'arbre (jiri = arbre, fe = pres de)", highlight: [1] },
    ],
  },
  {
    id: "revolue",
    pattern: "Sujet + YE + Verbe + TUN",
    label: "Passe revolu (imparfait)",
    description: "TUN place apres le verbe indique un passe revolu : ce qui etait, ce qu'on avait fait.",
    color: "#4a148c",
    emoji: "⏳",
    examples: [
      { bambara: "A ye taa tun", french: "Il etait parti / Il avait ete parti", highlight: [1, 3] },
      { bambara: "N ye dun tun", french: "J'avais mange", highlight: [1, 3] },
      { bambara: "U ye kuma tun", french: "Ils avaient parle", highlight: [1, 3] },
    ],
  },
];

// ─── Utilitaire : trouver l'annotation d'un mot ────────────────────
export function getWordAnnotation(
  song: BambaraSong,
  word: string
): WordAnnotation | undefined {
  return song.wordAnnotations[word] ?? song.wordAnnotations[word.toLowerCase()];
}

// ─── CHANSONS WOLOF ───────────────────────────────────────────────
// À compléter — structure identique aux chansons bambara

export const WOLOF_SONGS: BambaraSong[] = [
  // ── Chanson 1 : Teranga ──────────────────────────────────────────
  {
    id: "teranga",
    title: "Teranga",
    artist: "Style Mbalax traditionnel",
    genre: "Mbalax",
    theme: "L'hospitalité sénégalaise",
    emoji: "🤝",
    difficulty: "debutant",
    lines: [
      {
        words: ["Teranga", "bi"],
        translation: "L'hospitalité",
        structure: "Nom + Article défini",
        grammarNote: "BI est l'article défini singulier en wolof. Il se place après le nom.",
      },
      {
        words: ["Sénégaal", "dafa", "am"],
        translation: "Le Sénégal en a",
        structure: "Sujet + DAFA + Verbe",
        grammarNote: "DAFA est un prédicat verbal affirmatif de la 3e personne. AM = avoir/posséder.",
      },
      {
        words: ["Jëf", "ak", "xam-xam"],
        translation: "L'action et le savoir",
        structure: "Nom + AK + Nom",
        grammarNote: "AK est la conjonction « et / avec » en wolof. XAM-XAM = connaissance (réduplication).",
      },
    ],
    wordAnnotations: {
      Teranga: {
        translation: "hospitalité / accueil",
        grammaticalCategory: "nom",
        grammarRule: "TERANGA est le concept sénégalais d'hospitalité et d'accueil généreux. C'est la valeur culturelle fondamentale du Sénégal, connue dans le monde entier.",
        example: "Teranga bi dafa rafet.",
        exampleTranslation: "L'hospitalité est belle.",
      },
      bi: {
        translation: "le / la (article défini)",
        grammaticalCategory: "determinant",
        grammarRule: "BI est l'article défini singulier de classe B en wolof. Les articles se placent APRÈS le nom en wolof.",
        example: "Nit bi.",
        exampleTranslation: "L'homme.",
      },
      dafa: {
        translation: "(prédicat verbal 3e pers. sing. affirmatif)",
        grammaticalCategory: "marqueur",
        grammarRule: "DAFA est un prédicat verbal qui affirme et focalise l'action. Sujet implicite 3e personne. Structure : DAFA + Verbe → « il/elle fait vraiment ».",
        example: "Dafa dem.",
        exampleTranslation: "Il est parti.",
      },
      am: {
        translation: "avoir / posséder",
        grammaticalCategory: "verbe",
        grammarRule: "AM signifie « avoir, posséder ». Très courant en wolof. MAA AM = c'est moi qui ai. DAFA AM = il/elle a.",
        example: "Dafa am xarit.",
        exampleTranslation: "Il a un ami.",
      },
      Jëf: {
        translation: "action / acte / travail",
        grammaticalCategory: "nom",
        grammarRule: "JËF désigne une action, un acte. Aussi utilisé comme verbe : « agir, faire ». JËF BU BAAX = une bonne action.",
        example: "Jëf bi dafa baax.",
        exampleTranslation: "L'action est bonne.",
      },
      ak: {
        translation: "et / avec",
        grammaticalCategory: "connecteur",
        grammarRule: "AK est la conjonction principale du wolof : « et, avec ». Se place entre deux noms ou groupes nominaux.",
        example: "Mu ak man.",
        exampleTranslation: "Lui et moi.",
      },
      "xam-xam": {
        translation: "savoir / connaissance",
        grammaticalCategory: "nom",
        grammarRule: "XAM-XAM est formé par la réduplication de XAM (savoir/connaître). La réduplication indique l'intensité ou la généralisation. XAM-XAM = la connaissance en général.",
        example: "Xam-xam dafa am solo.",
        exampleTranslation: "La connaissance a de la valeur.",
      },
    },
  },
];

export const SONGS_BY_LANGUAGE: Record<string, BambaraSong[]> = {
  ba: BAMBARA_SONGS,
  wf: WOLOF_SONGS,
};
